import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { speakText, stopSpeaking } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { supabase } from '../supabase';
import './Luna.css';
import { getTopicBySlug, getTopicName } from '../data/topicCatalog';
import { spanishGrammarTopics, frenchGrammarTopics, portugueseGrammarTopics, EXTRA_GRAMMAR_TEXT } from '../data/gabiGrammarTopics';

const languageNameMap = { italian: 'Italian', english: 'English', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese' };
const languageCountryMap = { italian: 'Italy', english: 'an English-speaking country', spanish: 'Spain', french: 'France', portuguese: 'Brazil' };

// Languages that have a formal and an informal way of saying "you"
const hasFormalRegister = { italian: true, spanish: true, french: true, portuguese: true, english: false };
const formalYouLabels = { italian: 'Lei', spanish: 'usted', french: 'vous', portuguese: 'o senhor / a senhora' };
const informalYouLabels = { italian: 'tu', spanish: 'tú', french: 'tu', portuguese: 'você' };

const lunaStrings = {
    italian: {
        trouble: "Scusa, ho avuto un problema. Riprova tra poco!",
        didntHear: "Non ho sentito bene — puoi ripetere?",
        audioFailed: "Scusa, ho avuto un problema con l'audio. Riprova!",
        placeholder: 'Scrivi in italiano...',
    },
    english: {
        trouble: 'Sorry, I had a problem. Please try again in a moment!',
        didntHear: "I didn't catch that. Could you say it again?",
        audioFailed: 'Sorry, there was a problem with the audio. Please try again!',
        placeholder: 'Write in English...',
    },
    spanish: {
        trouble: 'Lo siento, tuve un problema. ¡Inténtalo de nuevo en un momento!',
        didntHear: 'No te he oído bien. ¿Puedes repetirlo?',
        audioFailed: 'Lo siento, hubo un problema con el audio. ¡Inténtalo de nuevo!',
        placeholder: 'Escribe en español...',
    },
    french: {
        trouble: "Désolée, j'ai eu un problème. Réessaie dans un instant !",
        didntHear: "Je n'ai pas bien entendu. Tu peux répéter ?",
        audioFailed: "Désolée, il y a eu un problème avec l'audio. Réessaie !",
        placeholder: 'Écris en français...',
    },
    portuguese: {
        trouble: 'Desculpe, tive um problema. Tente de novo daqui a pouco!',
        didntHear: 'Não ouvi bem. Pode repetir?',
        audioFailed: 'Desculpe, houve um problema com o áudio. Tente de novo!',
        placeholder: 'Escreva em português...',
    },
};

const otherGreetings = {
    english: (name) => [
        `Hi${name}! I'm Gabi. How are you today?`,
        `Hey${name}! Great to see you. What shall we talk about today?`,
        `Hello${name}! How has your day been so far?`,
        `Hi${name}! I'm curious, what was the best part of your day?`,
    ],
    spanish: (name) => [
        `¡Hola${name}! Soy Gabi. ¿Cómo estás hoy?`,
        `¡Hola${name}! Qué bueno verte. ¿De qué hablamos hoy?`,
        `¡Ey${name}! ¿Cómo va todo? Cuéntame algo.`,
        `¡Hola${name}! Tengo curiosidad: ¿cuál fue lo mejor de tu día?`,
    ],
    french: (name) => [
        `Salut${name} ! Je suis Gabi. Comment vas-tu aujourd'hui ?`,
        `Coucou${name} ! Ça me fait plaisir de te voir. De quoi on parle aujourd'hui ?`,
        `Salut${name} ! Alors, comment s'est passée ta journée ?`,
        `Salut${name} ! Je suis curieuse : quel a été le meilleur moment de ta journée ?`,
    ],
    portuguese: (name) => [
        `Oi${name}! Eu sou a Gabi. Como você está hoje?`,
        `Oi${name}! Que bom te ver. Sobre o que vamos conversar hoje?`,
        `Olá${name}! Como foi o seu dia até agora?`,
        `Oi${name}! Estou curiosa: qual foi o melhor momento do seu dia?`,
    ],
};

// Scenario descriptions are written for Italian. This adapts them to the language being practised.
const localizeScenarioPrompt = (prompt, lang) => prompt
    .replace(/Italian/g, languageNameMap[lang] || 'Italian')
    .replace(/Italy/g, languageCountryMap[lang] || 'Italy')
    .replace(/\ban (Spanish|French|Portuguese)\b/g, 'a $1')
    .replace(/informal tu/g, 'the informal form of "you"')
    .replace(/use tu/g, 'use the informal form of "you"');

const buildRegisterNote = (register, lang) => {
    if (!hasFormalRegister[lang]) return '';
    const formal = formalYouLabels[lang];
    const informal = informalYouLabels[lang];
    return register === 'formal'
        ? ` REGISTER: This situation requires the FORMAL register ("${formal}"). The student should address you formally. If they use the informal "${informal}" forms with you, treat it as an error worth correcting (per your correction style) and politely note that the formal form is expected here. You yourself must consistently model the correct register.`
        : ` REGISTER: This situation is INFORMAL ("${informal}"). The student should address you informally. If they use overly formal forms, gently note (per your correction style) that with friends it sounds too distant. You yourself must consistently model the correct register.`;
};

const englishGrammarTopics = [
    { id: 'past-simple', name: 'Past simple', desc: 'Finished actions in the past', explanation: 'Used for actions that started and finished at a specific time in the past. Example: "I visited Rome last year", "She didn\'t call me yesterday".' },
    { id: 'present-perfect', name: 'Present perfect', desc: 'Past actions connected to now', explanation: 'Used for experiences, changes, and situations that continue up to now. Formed with have/has + past participle. Example: "I have lived here for five years", "Have you ever tried sushi?"' },
    { id: 'present-continuous', name: 'Present continuous', desc: 'Actions happening now', explanation: 'Used for actions happening at this moment or around now. Formed with am/is/are + verb-ing. Example: "I\'m cooking dinner", "They\'re studying right now".' },
    { id: 'future-forms', name: 'Future forms', desc: 'Will, going to and plans', explanation: 'English has several ways to talk about the future. "Going to" is for plans, "will" is for decisions and predictions, and the present continuous is for fixed arrangements. Example: "I\'m going to visit my parents", "I\'ll help you with that", "I\'m meeting Anna at six".' },
    { id: 'conditionals', name: 'Conditionals', desc: 'If this happens, then...', explanation: 'Used to talk about real or imagined situations and their results. Example: "If it rains, I\'ll stay home" (real), "If I had more time, I would learn Italian" (imagined).' },
    { id: 'modal-verbs', name: 'Modal verbs', desc: 'Can, should, must, might', explanation: 'Small helper verbs that express ability, advice, obligation, and possibility. Example: "You should see a doctor", "She might be late", "I can\'t come tonight".' },
    { id: 'phrasal-verbs', name: 'Phrasal verbs', desc: 'Verb + particle, like give up', explanation: 'A verb combined with a small word that changes its meaning. Very common in everyday English. Example: "I gave up smoking", "Can you turn the music down?", "We ran out of milk".' },
    { id: 'articles', name: 'Articles', desc: 'A, an and the', explanation: 'Use "a/an" for something general or mentioned for the first time, and "the" for something specific or already known. Example: "I saw a dog. The dog was huge.", "She is an engineer".' },
    { id: 'comparatives', name: 'Comparatives and superlatives', desc: 'Comparing things', explanation: 'Used to compare things: "more/less than" and "the most". Example: "This is cheaper than that one", "It\'s the best film I\'ve ever seen".' },
    { id: 'passive-voice', name: 'Passive voice', desc: 'When the action matters most', explanation: 'Used when the action or the result matters more than who did it. Formed with be + past participle. Example: "The bridge was built in 1920", "English is spoken all over the world".' },
    { id: 'reported-speech', name: 'Reported speech', desc: 'Telling what someone said', explanation: 'Used to report what someone said without quoting them exactly. The tense usually moves back one step. Example: "She said she was tired", "He asked me where I lived".' },
];

const rolePlayScenarios = [
    { id: 'cafe', emoji: '☕', name: 'At the cafe', desc: 'Order from a barista', register: 'formal', prompt: 'You are a friendly barista in an Italian cafe. The student is a customer ordering.', opener: 'Buongiorno! Benvenuto al bar. Cosa desidera?' },
    { id: 'market', emoji: '🍅', name: 'At the market', desc: 'Buy from a vendor', register: 'formal', prompt: 'You are a market vendor in Italy selling fruit and vegetables. The student is a customer.', opener: 'Buongiorno! Frutta fresca oggi! Cosa le serve?' },
    { id: 'restaurant', emoji: '🍝', name: 'At the restaurant', desc: 'Order dinner from a waiter', register: 'formal', prompt: 'You are a waiter in a casual Italian trattoria. The student is a customer.', opener: 'Buonasera! Benvenuti. Avete già scelto?' },
    { id: 'teacher', emoji: '📚', name: 'Teacher & student', desc: 'Talk to your teacher', register: 'informal', prompt: 'You are a friendly Italian school teacher. The student is your pupil discussing their studies. You address the student with informal tu, as teachers do with their pupils.', opener: 'Ciao! Come vanno i tuoi studi?' },
    { id: 'boss', emoji: '💼', name: 'Boss & employee', desc: 'A work conversation', register: 'formal', prompt: 'You are a friendly but professional Italian boss. The student is your employee. Discuss work naturally.', opener: 'Buongiorno! Ha un minuto per parlare del progetto?' },
    { id: 'doctor', emoji: '🩺', name: 'At the doctor', desc: 'Describe how you feel', register: 'formal', prompt: 'You are a kind Italian doctor. The student is a patient describing symptoms.', opener: 'Buongiorno, si accomodi. Come si sente oggi?' },
    { id: 'pharmacy', emoji: '💊', name: 'At the pharmacy', desc: 'Ask for medicine', register: 'formal', prompt: 'You are an Italian pharmacist. The student is a customer asking for advice.', opener: 'Buongiorno! Come posso aiutarla?' },
    { id: 'train', emoji: '🚆', name: 'Train station', desc: 'Buy a ticket', register: 'formal', prompt: 'You are a ticket clerk at an Italian train station. The student needs to buy a ticket.', opener: 'Buongiorno! Dove deve andare?' },
    { id: 'directions', emoji: '🗺️', name: 'Asking directions', desc: 'Find your way', register: 'formal', prompt: 'You are a helpful passerby in an Italian city. The student is lost and asking for directions.', opener: 'Mi scusi, ha bisogno di aiuto?' },
    { id: 'hotel', emoji: '🏨', name: 'Hotel check-in', desc: 'Check into a hotel', register: 'formal', prompt: 'You are an Italian hotel receptionist. The student is checking in.', opener: 'Buonasera! Benvenuti. Ha una prenotazione?' },
    { id: 'neighbor', emoji: '🏠', name: 'New neighbor', desc: 'Chat in the hallway', register: 'informal', prompt: 'You are the student\'s new Italian neighbor chatting in the hallway. You are around the same age, so you naturally use informal tu.', opener: 'Ciao! Sei nuova qui, vero? Io abito al terzo piano.' },
    { id: 'taxi', emoji: '🚕', name: 'In a taxi', desc: 'Tell the driver where to go', register: 'formal', prompt: 'You are a chatty Italian taxi driver. The student is your passenger.', opener: 'Buongiorno! Dove la porto?' },
    { id: 'clothes', emoji: '👗', name: 'Clothes shopping', desc: 'Find the right size', register: 'formal', prompt: 'You are a shop assistant in an Italian clothing store. The student is a customer.', opener: 'Buongiorno! Posso aiutarla a trovare qualcosa?' },
    { id: 'hairdresser', emoji: '💇', name: 'At the hairdresser', desc: 'Describe what you want', register: 'informal', prompt: 'You are an Italian hairdresser. The student is a regular client, so the tone is friendly and informal.', opener: 'Ciao! Allora, cosa facciamo oggi?' },
    { id: 'gym', emoji: '🏋️', name: 'At the gym', desc: 'Sign up and ask questions', register: 'informal', prompt: 'You are a trainer at an Italian gym. The student wants to sign up. Gym culture is informal — use tu.', opener: 'Ciao! Benvenuto in palestra. È la prima volta qui?' },
    { id: 'bank', emoji: '🏦', name: 'At the bank', desc: 'Open an account', register: 'formal', prompt: 'You are an Italian bank clerk. The student needs to open an account.', opener: 'Buongiorno! Come posso aiutarla oggi?' },
    { id: 'postoffice', emoji: '📮', name: 'Post office', desc: 'Send a package', register: 'formal', prompt: 'You are an Italian post office clerk. The student wants to send a package.', opener: 'Buongiorno! Cosa deve spedire?' },
    { id: 'landlord', emoji: '🔑', name: 'Renting an apartment', desc: 'Ask about a flat', register: 'formal', prompt: 'You are an Italian landlord showing an apartment. The student is a potential tenant.', opener: 'Buongiorno! Ecco l\'appartamento. Che ne pensa?' },
    { id: 'jobinterview', emoji: '🤝', name: 'Job interview', desc: 'Answer interview questions', register: 'formal', prompt: 'You are an Italian hiring manager conducting a relaxed job interview with the student.', opener: 'Buongiorno! Grazie di essere venuta. Mi parli un po\' di lei.' },
    { id: 'friend', emoji: '😄', name: 'Old friend', desc: 'Catch up over coffee', register: 'informal', prompt: 'You are the student\'s old Italian friend catching up over coffee.', opener: 'Ehi, quanto tempo! Come stai? Raccontami tutto!' },
    { id: 'party', emoji: '🎉', name: 'At a party', desc: 'Meet someone new', register: 'informal', prompt: 'You are a friendly Italian person the student has just met at a party. Parties among peers are informal — use tu.', opener: 'Ciao! Non ci conosciamo, vero? Io sono un\'amica di Marco.' },
    { id: 'airport', emoji: '✈️', name: 'At the airport', desc: 'Check in for a flight', register: 'formal', prompt: 'You are an Italian airport check-in agent. The student is a traveler.', opener: 'Buongiorno! Passaporto e biglietto, per favore.' },
    { id: 'supermarket', emoji: '🛒', name: 'Supermarket', desc: 'Find what you need', register: 'formal', prompt: 'You are an Italian supermarket employee. The student is looking for items.', opener: 'Buongiorno! Sta cercando qualcosa?' },
    { id: 'gelateria', emoji: '🍨', name: 'Gelateria', desc: 'Choose your flavors', register: 'informal', prompt: 'You work at an Italian gelateria. The student is choosing gelato. Gelaterias are casual and friendly — use tu.', opener: 'Ciao! Che gusti preferisci? Abbiamo tutto fresco oggi!' },
    { id: 'mechanic', emoji: '🔧', name: 'Car trouble', desc: 'Explain the problem', register: 'formal', prompt: 'You are an Italian mechanic. The student\'s car has a problem.', opener: 'Buongiorno! Allora, che problema ha la macchina?' },
    { id: 'police', emoji: '👮', name: 'Lost wallet', desc: 'Report something lost', register: 'formal', prompt: 'You are a patient Italian police officer. The student is reporting a lost wallet.', opener: 'Buongiorno. Mi dica pure, cosa è successo?' },
    { id: 'vet', emoji: '🐕', name: 'At the vet', desc: 'Bring your pet in', register: 'formal', prompt: 'You are an Italian veterinarian. The student brought their pet in.', opener: 'Buongiorno! E chi è questo bel cucciolo? Cosa succede?' },
    { id: 'bookshop', emoji: '📖', name: 'Bookshop', desc: 'Ask for a recommendation', register: 'formal', prompt: 'You are an Italian bookshop owner. The student wants a book recommendation.', opener: 'Buongiorno! Cerca qualcosa in particolare da leggere?' },
    { id: 'weekend', emoji: '🌤️', name: 'Weekend plans', desc: 'Make plans with a friend', register: 'informal', prompt: 'You are the student\'s Italian friend making weekend plans together.', opener: 'Ciao! Allora, che facciamo questo weekend?' },
    { id: 'cooking', emoji: '👨‍🍳', name: 'Cooking class', desc: 'Learn a recipe', register: 'informal', prompt: 'You are an Italian cooking instructor teaching the student a recipe. Cooking classes are friendly and hands-on — use tu.', opener: 'Che bello averti in cucina! Oggi facciamo la pasta fresca. Si comincia?' },
];

const freeGreetings = (name, topic) => [
    `Ciao${name}! Sono Gabi. Parliamo un po'${topic}! Come stai oggi?`,
    `Ciao${name}! Che bello vederti! Di cosa parliamo oggi?`,
    `Ehi${name}, ciao! Come va? Raccontami qualcosa!`,
    `Ciao${name}! Sono contenta di parlare con te. Com'è andata la tua giornata?`,
    `Buongiorno${name}! Parliamo un po' di italiano? Come ti senti oggi?`,
    `Ciao${name}! Indovina un po' — cosa hai fatto di bello oggi?`,
    `Ehi${name}! Ho voglia di chiacchierare. Dimmi, come va la settimana?`,
    `Ciao${name}, bentornata! Cosa mi racconti di nuovo?`,
    `Ciao${name}! Sono curiosa — qual è stato il momento migliore della tua giornata?`,
    `Ehi${name}! Parliamo un po'? Da dove iniziamo oggi?`,
    `Ciao${name}! Che piacere sentirti. Allora, tutto bene?`,
];

const italianGrammarTopics = [
    {
        id: 'passato-prossimo', name: 'Passato prossimo', desc: 'Talking about completed actions',
        explanation: 'Used for actions completed in the past — things that happened once and are finished. Formed with avere or essere + past participle. Example: "Ho mangiato la pasta" (I ate the pasta), "Sono andata al mare" (I went to the sea).',
    },
    {
        id: 'imperfetto', name: 'Imperfetto', desc: 'Describing the past, habits',
        explanation: 'Used for ongoing or repeated actions in the past, descriptions, and background situations — not a single completed event. Example: "Da bambino giocavo sempre fuori" (As a child I always used to play outside), "Faceva bel tempo" (The weather was nice).',
    },
    {
        id: 'futuro-semplice', name: 'Futuro semplice', desc: 'Talking about the future',
        explanation: 'Used to talk about future plans, predictions, and promises. Example: "Domani andrò al lavoro" (Tomorrow I will go to work), "Sarà bellissimo" (It will be beautiful).',
    },
    {
        id: 'condizionale', name: 'Condizionale', desc: 'Would / could — polite requests',
        explanation: 'Used to express "would" — polite requests, hypothetical wishes, and softened statements. Example: "Vorrei un caffè" (I would like a coffee), "Potresti aiutarmi?" (Could you help me?).',
    },
    {
        id: 'congiuntivo-presente', name: 'Congiuntivo presente', desc: 'Opinions, doubts, wishes',
        explanation: 'Used after expressions of opinion, doubt, wish or emotion — usually after "che". Example: "Penso che sia vero" (I think it\'s true), "Spero che tu stia bene" (I hope you\'re well).',
    },
    {
        id: 'gerundio', name: 'Gerundio', desc: 'Ongoing actions — stare + gerundio',
        explanation: 'Used with "stare" to describe an action happening right now, like English "-ing". Example: "Sto mangiando" (I am eating), "Stavo dormendo" (I was sleeping).',
    },
    {
        id: 'pronomi-diretti', name: 'Pronomi diretti', desc: 'Direct object pronouns — lo, la, li, le',
        explanation: 'Used to replace a direct object already mentioned, avoiding repetition. Example: "Vedi il libro? Lo vedo." (Do you see the book? I see it.) — lo/la/li/le depending on gender and number.',
    },
    {
        id: 'pronomi-indiretti', name: 'Pronomi indiretti', desc: 'Indirect object pronouns — gli, le, mi, ti',
        explanation: 'Used to replace the person something is given or said "to" — mi, ti, gli, le, ci, vi, gli. Example: "Gli ho scritto una email" (I wrote him an email), "Mi piace" (It is pleasing to me / I like it).',
    },
    {
        id: 'preposizioni', name: 'Preposizioni', desc: 'Prepositions — a, di, da, in, con, su',
        explanation: 'Small words that show relationships between other words — location, direction, possession, time. Example: "Vado a Roma" (I go to Rome), "Il libro di Marco" (Marco\'s book), "Sono di Milano" (I\'m from Milan).',
    },
    {
        id: 'comparativi', name: 'Comparativi e superlativi', desc: 'Comparing things — più, meno, il più',
        explanation: 'Used to compare things — "more/less than" (più/meno di) and "the most" (il più). Example: "È più alto di me" (He\'s taller than me), "È il più bravo della classe" (He\'s the best in the class).',
    },
    {
        id: 'imperativo', name: 'Imperativo', desc: 'Giving commands and instructions',
        explanation: 'Used to give commands, instructions, or advice directly. Example: "Vieni qui!" (Come here!), "Non parlare così!" (Don\'t talk like that!).',
    },
    {
        id: 'ci-ne', name: 'Ci e ne', desc: 'The tricky little particles ci and ne',
        explanation: '"Ci" replaces a place or a thing already mentioned ("Vai al mare? Sì, ci vado" — Are you going to the sea? Yes, I\'m going there). "Ne" replaces "of it/them" for quantities ("Quanti ne vuoi? Ne voglio due" — How many do you want? I want two of them).',
    },
    {
        id: 'si-impersonale', name: 'Si impersonale', desc: 'The impersonal "si" — one does, people do',
        explanation: 'Used to talk about what "people" or "one" generally does, without naming who — very common in everyday Italian. Example: "Qui si mangia bene" (People eat well here / The food here is good), "Come si dice...?" (How does one say...?), "Non si può fumare" (You can\'t smoke / Smoking isn\'t allowed).',
    },
];

const grammarTopicsByLanguage = {
    italian: italianGrammarTopics,
    english: englishGrammarTopics,
    spanish: spanishGrammarTopics,
    french: frenchGrammarTopics,
    portuguese: portugueseGrammarTopics,
};

// ---------- Screen text, shown in the interface language ----------
const TEXT = {
    en: {
        practiceWith: 'Practice speaking with Gabi',
        topicLabel: 'Topic: {topic}',
        freeName: 'Free conversation',
        freeDesc: 'Chat naturally with Gabi about this topic',
        roleName: 'Role-play scenario',
        roleDesc: 'Gabi plays a real-life character — practice a real situation',
        grammarName: 'Grammar practice',
        grammarDesc: 'Focus your speaking on one grammar structure',
        correctLabel: 'How should Gabi correct you?',
        gentle: 'Gentle',
        gentleDesc: 'Only important mistakes — keeps the conversation flowing',
        strict: 'Strict',
        strictDesc: 'Every article, verb and word — for maximum precision',
        history: '📜 View my past conversations',
        chooseGrammar: 'Choose a grammar topic',
        ownTopic: 'Type your own topic',
        eg: 'e.g.',
        grammarPlaceholder: 'the grammar topic you want to practise...',
        pickCommon: 'Or pick a common topic',
        howWhen: 'How and when to use it',
        startPractice: 'Start practicing →',
        chooseAnother: 'Choose another',
        pastTitle: 'Past conversations',
        loading: 'Loading...',
        noHistory: "No saved conversations yet — finish a chat with Gabi and it'll show up here.",
        messagesCount: '{n} messages',
        close: 'Close',
        chooseScenario: 'Choose a scenario',
        grammarLabel: 'Grammar',
        voiceSpeed: 'Voice speed',
        transcribingYour: 'Transcribing your {language}...',
        holdToSpeak: 'Hold to speak',
        transcribing: 'Transcribing...',
        slideLock: '◀ slide to lock · release to send',
        checkMy: 'Check my {language}',
        yourMessage: 'Your message',
        feedback: 'Feedback',
        checkingYour: 'Checking your {language}...',
        hearIt: '🔊 Hear it',
        saved: '✓ Saved to your Word Bank!',
        alreadyIn: '"{word}" is already in your Word Bank.',
        relatedBefore: 'You already have ',
        relatedAfter: ' in your Word Bank — "{word}" looks like a related form of the same word. Save it separately?',
        saveAnyway: 'Yes, save "{word}" anyway →',
        saving: 'Saving...',
        checking: 'Checking...',
        saveToBank: '🔖 Save to Word Bank',
        translateFail: 'Translation unavailable — try again.',
        feedbackFail: 'Feedback is temporarily unavailable — please try again shortly.',
        errTrouble: 'Gabi is temporarily unavailable',
        errDidntHear: "I couldn't hear that — try again!",
        errAudio: 'Audio processing failed — try again',
    },
    it: {
        practiceWith: 'Esercitati a parlare con Gabi',
        topicLabel: 'Argomento: {topic}',
        freeName: 'Conversazione libera',
        freeDesc: 'Chiacchiera con Gabi su questo argomento',
        roleName: 'Gioco di ruolo',
        roleDesc: 'Gabi interpreta un personaggio reale: allenati in una situazione concreta',
        grammarName: 'Pratica di grammatica',
        grammarDesc: 'Concentra il parlato su una struttura grammaticale',
        correctLabel: 'Come deve correggerti Gabi?',
        gentle: 'Gentile',
        gentleDesc: 'Solo gli errori importanti: la conversazione scorre',
        strict: 'Rigorosa',
        strictDesc: 'Ogni articolo, verbo e parola: per la massima precisione',
        history: '📜 Vedi le mie conversazioni passate',
        chooseGrammar: 'Scegli un argomento di grammatica',
        ownTopic: 'Scrivi un argomento a tua scelta',
        eg: 'es.',
        grammarPlaceholder: 'l’argomento di grammatica che vuoi esercitare...',
        pickCommon: 'Oppure scegli un argomento comune',
        howWhen: 'Come e quando si usa',
        startPractice: 'Inizia a esercitarti →',
        chooseAnother: 'Scegline un altro',
        pastTitle: 'Conversazioni passate',
        loading: 'Caricamento...',
        noHistory: 'Nessuna conversazione salvata: finisci una chat con Gabi e comparirà qui.',
        messagesCount: '{n} messaggi',
        close: 'Chiudi',
        chooseScenario: 'Scegli una situazione',
        grammarLabel: 'Grammatica',
        voiceSpeed: 'Velocità della voce',
        transcribingYour: 'Sto trascrivendo il tuo {language}...',
        holdToSpeak: 'Tieni premuto per parlare',
        transcribing: 'Trascrizione...',
        slideLock: '◀ scorri per bloccare · rilascia per inviare',
        checkMy: 'Controlla il mio {language}',
        yourMessage: 'Il tuo messaggio',
        feedback: 'Feedback',
        checkingYour: 'Sto controllando il tuo {language}...',
        hearIt: '🔊 Ascolta',
        saved: '✓ Salvata nel tuo Word Bank!',
        alreadyIn: '"{word}" è già nel tuo Word Bank.',
        relatedBefore: 'Hai già ',
        relatedAfter: ' nel tuo Word Bank: "{word}" sembra una forma collegata della stessa parola. Vuoi salvarla comunque?',
        saveAnyway: 'Sì, salva "{word}" comunque →',
        saving: 'Salvataggio...',
        checking: 'Controllo...',
        saveToBank: '🔖 Salva nel Word Bank',
        translateFail: 'Traduzione non disponibile: riprova.',
        feedbackFail: 'Il feedback non è disponibile al momento: riprova tra poco.',
        errTrouble: 'Gabi non è disponibile al momento',
        errDidntHear: 'Non ho sentito bene, riprova!',
        errAudio: 'Elaborazione dell’audio non riuscita: riprova',
    },
    es: {
        practiceWith: 'Practica hablando con Gabi',
        topicLabel: 'Tema: {topic}',
        freeName: 'Conversación libre',
        freeDesc: 'Charla con Gabi sobre este tema',
        roleName: 'Juego de rol',
        roleDesc: 'Gabi interpreta a un personaje real: practica una situación de verdad',
        grammarName: 'Práctica de gramática',
        grammarDesc: 'Concentra tu práctica oral en una estructura gramatical',
        correctLabel: '¿Cómo debe corregirte Gabi?',
        gentle: 'Suave',
        gentleDesc: 'Solo los errores importantes: la conversación fluye',
        strict: 'Estricta',
        strictDesc: 'Cada artículo, verbo y palabra: para la máxima precisión',
        history: '📜 Ver mis conversaciones anteriores',
        chooseGrammar: 'Elige un tema de gramática',
        ownTopic: 'Escribe tu propio tema',
        eg: 'p. ej.',
        grammarPlaceholder: 'el tema de gramática que quieres practicar...',
        pickCommon: 'O elige un tema común',
        howWhen: 'Cómo y cuándo se usa',
        startPractice: 'Empezar a practicar →',
        chooseAnother: 'Elegir otro',
        pastTitle: 'Conversaciones anteriores',
        loading: 'Cargando...',
        noHistory: 'Todavía no hay conversaciones guardadas: termina un chat con Gabi y aparecerá aquí.',
        messagesCount: '{n} mensajes',
        close: 'Cerrar',
        chooseScenario: 'Elige una situación',
        grammarLabel: 'Gramática',
        voiceSpeed: 'Velocidad de la voz',
        transcribingYour: 'Transcribiendo tu {language}...',
        holdToSpeak: 'Mantén pulsado para hablar',
        transcribing: 'Transcribiendo...',
        slideLock: '◀ desliza para bloquear · suelta para enviar',
        checkMy: 'Revisa mi {language}',
        yourMessage: 'Tu mensaje',
        feedback: 'Comentarios',
        checkingYour: 'Revisando tu {language}...',
        hearIt: '🔊 Escuchar',
        saved: '✓ ¡Guardada en tu Word Bank!',
        alreadyIn: '"{word}" ya está en tu Word Bank.',
        relatedBefore: 'Ya tienes ',
        relatedAfter: ' en tu Word Bank: "{word}" parece una forma relacionada de la misma palabra. ¿Quieres guardarla por separado?',
        saveAnyway: 'Sí, guardar "{word}" de todos modos →',
        saving: 'Guardando...',
        checking: 'Comprobando...',
        saveToBank: '🔖 Guardar en el Word Bank',
        translateFail: 'Traducción no disponible: inténtalo de nuevo.',
        feedbackFail: 'Los comentarios no están disponibles ahora: inténtalo de nuevo en un momento.',
        errTrouble: 'Gabi no está disponible en este momento',
        errDidntHear: 'No he oído bien, ¡inténtalo de nuevo!',
        errAudio: 'Falló el procesamiento del audio: inténtalo de nuevo',
    },
    fr: {
        practiceWith: 'Entraîne-toi à parler avec Gabi',
        topicLabel: 'Sujet : {topic}',
        freeName: 'Conversation libre',
        freeDesc: 'Discute avec Gabi de ce sujet',
        roleName: 'Jeu de rôle',
        roleDesc: 'Gabi joue un personnage de la vie réelle : entraîne-toi sur une vraie situation',
        grammarName: 'Entraînement à la grammaire',
        grammarDesc: 'Concentre ton oral sur une structure grammaticale',
        correctLabel: 'Comment Gabi doit-elle te corriger ?',
        gentle: 'Douce',
        gentleDesc: 'Seulement les erreurs importantes : la conversation reste fluide',
        strict: 'Stricte',
        strictDesc: 'Chaque article, verbe et mot : pour une précision maximale',
        history: '📜 Voir mes conversations passées',
        chooseGrammar: 'Choisis un point de grammaire',
        ownTopic: 'Écris ton propre sujet',
        eg: 'p. ex.',
        grammarPlaceholder: 'le point de grammaire que tu veux travailler...',
        pickCommon: 'Ou choisis un sujet courant',
        howWhen: 'Comment et quand l’utiliser',
        startPractice: 'Commencer à s’entraîner →',
        chooseAnother: 'En choisir un autre',
        pastTitle: 'Conversations passées',
        loading: 'Chargement...',
        noHistory: 'Aucune conversation enregistrée pour le moment : termine un chat avec Gabi et il apparaîtra ici.',
        messagesCount: '{n} messages',
        close: 'Fermer',
        chooseScenario: 'Choisis une situation',
        grammarLabel: 'Grammaire',
        voiceSpeed: 'Vitesse de la voix',
        transcribingYour: 'Transcription de ton {language}...',
        holdToSpeak: 'Maintiens pour parler',
        transcribing: 'Transcription...',
        slideLock: '◀ glisse pour verrouiller · relâche pour envoyer',
        checkMy: 'Vérifier mon {language}',
        yourMessage: 'Ton message',
        feedback: 'Retour',
        checkingYour: 'Vérification de ton {language}...',
        hearIt: '🔊 Écouter',
        saved: '✓ Enregistré dans ton Word Bank !',
        alreadyIn: '"{word}" est déjà dans ton Word Bank.',
        relatedBefore: 'Tu as déjà ',
        relatedAfter: ' dans ton Word Bank : "{word}" semble être une forme liée du même mot. Veux-tu l’enregistrer séparément ?',
        saveAnyway: 'Oui, enregistrer "{word}" quand même →',
        saving: 'Enregistrement...',
        checking: 'Vérification...',
        saveToBank: '🔖 Enregistrer dans le Word Bank',
        translateFail: 'Traduction indisponible : réessaie.',
        feedbackFail: 'Le retour est momentanément indisponible : réessaie dans un instant.',
        errTrouble: 'Gabi est momentanément indisponible',
        errDidntHear: 'Je n’ai pas bien entendu, réessaie !',
        errAudio: 'Le traitement de l’audio a échoué : réessaie',
    },
    pt: {
        practiceWith: 'Pratique falando com a Gabi',
        topicLabel: 'Tópico: {topic}',
        freeName: 'Conversa livre',
        freeDesc: 'Converse com a Gabi sobre este tópico',
        roleName: 'Simulação de situação',
        roleDesc: 'A Gabi interpreta um personagem da vida real: pratique uma situação de verdade',
        grammarName: 'Prática de gramática',
        grammarDesc: 'Concentre sua fala em uma estrutura gramatical',
        correctLabel: 'Como a Gabi deve corrigir você?',
        gentle: 'Suave',
        gentleDesc: 'Só os erros importantes: a conversa continua fluindo',
        strict: 'Rigorosa',
        strictDesc: 'Cada artigo, verbo e palavra: para a máxima precisão',
        history: '📜 Ver minhas conversas anteriores',
        chooseGrammar: 'Escolha um tópico de gramática',
        ownTopic: 'Digite o seu próprio tópico',
        eg: 'p. ex.',
        grammarPlaceholder: 'o tópico de gramática que você quer praticar...',
        pickCommon: 'Ou escolha um tópico comum',
        howWhen: 'Como e quando usar',
        startPractice: 'Começar a praticar →',
        chooseAnother: 'Escolher outro',
        pastTitle: 'Conversas anteriores',
        loading: 'Carregando...',
        noHistory: 'Ainda não há conversas salvas: termine um chat com a Gabi e ele aparecerá aqui.',
        messagesCount: '{n} mensagens',
        close: 'Fechar',
        chooseScenario: 'Escolha uma situação',
        grammarLabel: 'Gramática',
        voiceSpeed: 'Velocidade da voz',
        transcribingYour: 'Transcrevendo o seu {language}...',
        holdToSpeak: 'Segure para falar',
        transcribing: 'Transcrevendo...',
        slideLock: '◀ deslize para travar · solte para enviar',
        checkMy: 'Verificar o meu {language}',
        yourMessage: 'Sua mensagem',
        feedback: 'Feedback',
        checkingYour: 'Verificando o seu {language}...',
        hearIt: '🔊 Ouvir',
        saved: '✓ Salva no seu Word Bank!',
        alreadyIn: '"{word}" já está no seu Word Bank.',
        relatedBefore: 'Você já tem ',
        relatedAfter: ' no seu Word Bank: "{word}" parece uma forma relacionada da mesma palavra. Quer salvá-la separadamente?',
        saveAnyway: 'Sim, salvar "{word}" mesmo assim →',
        saving: 'Salvando...',
        checking: 'Verificando...',
        saveToBank: '🔖 Salvar no Word Bank',
        translateFail: 'Tradução indisponível: tente de novo.',
        feedbackFail: 'O feedback está temporariamente indisponível: tente de novo daqui a pouco.',
        errTrouble: 'A Gabi está temporariamente indisponível',
        errDidntHear: 'Não ouvi bem, tente de novo!',
        errAudio: 'O processamento do áudio falhou: tente de novo',
    },
};

// Language names in each interface language
const LANG_LABEL = {
    en: { italian: 'Italian', english: 'English', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese' },
    it: { italian: 'italiano', english: 'inglese', spanish: 'spagnolo', french: 'francese', portuguese: 'portoghese' },
    es: { italian: 'italiano', english: 'inglés', spanish: 'español', french: 'francés', portuguese: 'portugués' },
    fr: { italian: 'italien', english: 'anglais', spanish: 'espagnol', french: 'français', portuguese: 'portugais' },
    pt: { italian: 'italiano', english: 'inglês', spanish: 'espanhol', french: 'francês', portuguese: 'português' },
};

const UI_TO_LANG = { en: 'english', it: 'italian', es: 'spanish', fr: 'french', pt: 'portuguese' };
const DATE_LOCALES = { en: 'en-GB', it: 'it-IT', es: 'es-ES', fr: 'fr-FR', pt: 'pt-BR' };

// Role-play scenario names and descriptions (English ones live in rolePlayScenarios)
const SCENARIO_TEXT = {
    cafe: { it: ['Al bar', 'Ordina da un barista'], es: ['En la cafetería', 'Pide a un barista'], fr: ['Au café', 'Commande auprès d’un barista'], pt: ['Na cafeteria', 'Peça a um barista'] },
    market: { it: ['Al mercato', 'Compra da un venditore'], es: ['En el mercado', 'Compra a un vendedor'], fr: ['Au marché', 'Achète à un vendeur'], pt: ['No mercado', 'Compre de um vendedor'] },
    restaurant: { it: ['Al ristorante', 'Ordina la cena a un cameriere'], es: ['En el restaurante', 'Pide la cena a un camarero'], fr: ['Au restaurant', 'Commande le dîner auprès d’un serveur'], pt: ['No restaurante', 'Peça o jantar a um garçom'] },
    teacher: { it: ['Insegnante e studente', 'Parla con il tuo insegnante'], es: ['Profesor y alumno', 'Habla con tu profesor'], fr: ['Professeur et élève', 'Parle avec ton professeur'], pt: ['Professor e aluno', 'Converse com o seu professor'] },
    boss: { it: ['Capo e dipendente', 'Una conversazione di lavoro'], es: ['Jefe y empleado', 'Una conversación de trabajo'], fr: ['Patron et employé', 'Une conversation de travail'], pt: ['Chefe e funcionário', 'Uma conversa de trabalho'] },
    doctor: { it: ['Dal medico', 'Descrivi come ti senti'], es: ['En el médico', 'Describe cómo te sientes'], fr: ['Chez le médecin', 'Décris comment tu te sens'], pt: ['No médico', 'Descreva como você se sente'] },
    pharmacy: { it: ['In farmacia', 'Chiedi un medicinale'], es: ['En la farmacia', 'Pide un medicamento'], fr: ['À la pharmacie', 'Demande un médicament'], pt: ['Na farmácia', 'Peça um remédio'] },
    train: { it: ['Stazione ferroviaria', 'Acquista un biglietto'], es: ['Estación de tren', 'Compra un billete'], fr: ['Gare', 'Achète un billet'], pt: ['Estação de trem', 'Compre uma passagem'] },
    directions: { it: ['Chiedere indicazioni', 'Trova la strada'], es: ['Pedir indicaciones', 'Encuentra tu camino'], fr: ['Demander son chemin', 'Trouve ton chemin'], pt: ['Pedindo direções', 'Encontre o seu caminho'] },
    hotel: { it: ['Check-in in hotel', 'Registrati in un hotel'], es: ['Check-in en el hotel', 'Regístrate en un hotel'], fr: ['Enregistrement à l’hôtel', 'Enregistre-toi dans un hôtel'], pt: ['Check-in no hotel', 'Faça o check-in em um hotel'] },
    neighbor: { it: ['Nuovo vicino', 'Chiacchiera sul pianerottolo'], es: ['Vecino nuevo', 'Charla en el pasillo'], fr: ['Nouveau voisin', 'Bavarde sur le palier'], pt: ['Novo vizinho', 'Converse no corredor'] },
    taxi: { it: ['In taxi', 'Dì al tassista dove andare'], es: ['En un taxi', 'Dile al taxista adónde ir'], fr: ['En taxi', 'Dis au chauffeur où aller'], pt: ['De táxi', 'Diga ao motorista aonde ir'] },
    clothes: { it: ['Shopping di vestiti', 'Trova la taglia giusta'], es: ['De compras de ropa', 'Encuentra la talla adecuada'], fr: ['Shopping vêtements', 'Trouve la bonne taille'], pt: ['Compras de roupas', 'Encontre o tamanho certo'] },
    hairdresser: { it: ['Dal parrucchiere', 'Descrivi cosa vuoi'], es: ['En la peluquería', 'Describe lo que quieres'], fr: ['Chez le coiffeur', 'Décris ce que tu veux'], pt: ['No cabeleireiro', 'Descreva o que você quer'] },
    gym: { it: ['In palestra', 'Iscriviti e fai domande'], es: ['En el gimnasio', 'Apúntate y haz preguntas'], fr: ['À la salle de sport', 'Inscris-toi et pose des questions'], pt: ['Na academia', 'Inscreva-se e faça perguntas'] },
    bank: { it: ['In banca', 'Apri un conto'], es: ['En el banco', 'Abre una cuenta'], fr: ['À la banque', 'Ouvre un compte'], pt: ['No banco', 'Abra uma conta'] },
    postoffice: { it: ['Ufficio postale', 'Spedisci un pacco'], es: ['Oficina de correos', 'Envía un paquete'], fr: ['Bureau de poste', 'Envoie un colis'], pt: ['Correios', 'Envie um pacote'] },
    landlord: { it: ['Affittare un appartamento', 'Chiedi informazioni su un appartamento'], es: ['Alquilar un piso', 'Pregunta por un piso'], fr: ['Louer un appartement', 'Renseigne-toi sur un appartement'], pt: ['Alugando um apartamento', 'Pergunte sobre um apartamento'] },
    jobinterview: { it: ['Colloquio di lavoro', 'Rispondi alle domande del colloquio'], es: ['Entrevista de trabajo', 'Responde a las preguntas de la entrevista'], fr: ['Entretien d’embauche', 'Réponds aux questions de l’entretien'], pt: ['Entrevista de emprego', 'Responda às perguntas da entrevista'] },
    friend: { it: ['Vecchio amico', 'Fai due chiacchiere davanti a un caffè'], es: ['Un viejo amigo', 'Ponte al día con un café'], fr: ['Un vieil ami', 'Prends des nouvelles autour d’un café'], pt: ['Um velho amigo', 'Ponha o papo em dia tomando um café'] },
    party: { it: ['A una festa', 'Conosci una persona nuova'], es: ['En una fiesta', 'Conoce a alguien nuevo'], fr: ['À une fête', 'Rencontre quelqu’un de nouveau'], pt: ['Em uma festa', 'Conheça alguém novo'] },
    airport: { it: ['In aeroporto', 'Fai il check-in per un volo'], es: ['En el aeropuerto', 'Haz el check-in de un vuelo'], fr: ['À l’aéroport', 'Enregistre-toi pour un vol'], pt: ['No aeroporto', 'Faça o check-in de um voo'] },
    supermarket: { it: ['Supermercato', 'Trova quello che ti serve'], es: ['Supermercado', 'Encuentra lo que necesitas'], fr: ['Supermarché', 'Trouve ce dont tu as besoin'], pt: ['Supermercado', 'Encontre o que você precisa'] },
    gelateria: { it: ['Gelateria', 'Scegli i tuoi gusti'], es: ['Heladería', 'Elige tus sabores'], fr: ['Glacier', 'Choisis tes parfums'], pt: ['Sorveteria', 'Escolha seus sabores'] },
    mechanic: { it: ['Problemi con l’auto', 'Spiega il problema'], es: ['Problemas con el coche', 'Explica el problema'], fr: ['Panne de voiture', 'Explique le problème'], pt: ['Problema com o carro', 'Explique o problema'] },
    police: { it: ['Portafoglio smarrito', 'Denuncia uno smarrimento'], es: ['Cartera perdida', 'Denuncia una pérdida'], fr: ['Portefeuille perdu', 'Signale un objet perdu'], pt: ['Carteira perdida', 'Relate um objeto perdido'] },
    vet: { it: ['Dal veterinario', 'Porta il tuo animale'], es: ['En el veterinario', 'Lleva a tu mascota'], fr: ['Chez le vétérinaire', 'Amène ton animal'], pt: ['No veterinário', 'Leve o seu bichinho'] },
    bookshop: { it: ['Libreria', 'Chiedi un consiglio'], es: ['Librería', 'Pide una recomendación'], fr: ['Librairie', 'Demande une recommandation'], pt: ['Livraria', 'Peça uma recomendação'] },
    weekend: { it: ['Programmi per il weekend', 'Fai programmi con un amico'], es: ['Planes de fin de semana', 'Haz planes con un amigo'], fr: ['Projets de week-end', 'Fais des projets avec un ami'], pt: ['Planos de fim de semana', 'Faça planos com um amigo'] },
    cooking: { it: ['Corso di cucina', 'Impara una ricetta'], es: ['Clase de cocina', 'Aprende una receta'], fr: ['Cours de cuisine', 'Apprends une recette'], pt: ['Aula de culinária', 'Aprenda uma receita'] },
};

// Grammar topic descriptions and explanations in each interface language: [description, explanation]
const GRAMMAR_TEXT = {
    'passato-prossimo': {
        it: [`Parlare di azioni concluse`, `Si usa per azioni concluse nel passato: cose accadute una volta e ormai finite. Si forma con avere o essere + participio passato. Esempio: "Ho mangiato la pasta", "Sono andata al mare".`],
        es: [`Hablar de acciones terminadas`, `Se usa para acciones terminadas en el pasado: cosas que ocurrieron una vez y ya acabaron. Se forma con avere o essere + participio. Ejemplo: "Ho mangiato la pasta" (Comí la pasta), "Sono andata al mare" (Fui al mar).`],
        fr: [`Parler d’actions terminées`, `Sert à parler d’actions terminées dans le passé : des choses qui se sont produites une fois et sont finies. Se forme avec avere ou essere + participe passé. Exemple : "Ho mangiato la pasta" (J’ai mangé les pâtes), "Sono andata al mare" (Je suis allée à la mer).`],
        pt: [`Falar de ações concluídas`, `Usado para ações concluídas no passado: coisas que aconteceram uma vez e já terminaram. Forma-se com avere ou essere + particípio passado. Exemplo: "Ho mangiato la pasta" (Eu comi a massa), "Sono andata al mare" (Eu fui ao mar).`],
    },
    'imperfetto': {
        it: [`Descrivere il passato e le abitudini`, `Si usa per azioni in corso o ripetute nel passato, per descrizioni e situazioni di sfondo, non per un singolo evento concluso. Esempio: "Da bambino giocavo sempre fuori", "Faceva bel tempo".`],
        es: [`Describir el pasado y los hábitos`, `Se usa para acciones en curso o repetidas en el pasado, descripciones y situaciones de fondo, no para un único hecho terminado. Ejemplo: "Da bambino giocavo sempre fuori" (De niño siempre jugaba fuera), "Faceva bel tempo" (Hacía buen tiempo).`],
        fr: [`Décrire le passé et les habitudes`, `Sert pour des actions en cours ou répétées dans le passé, des descriptions et des situations d’arrière-plan, pas pour un événement unique et terminé. Exemple : "Da bambino giocavo sempre fuori" (Enfant, je jouais toujours dehors), "Faceva bel tempo" (Il faisait beau).`],
        pt: [`Descrever o passado e os hábitos`, `Usado para ações em andamento ou repetidas no passado, descrições e situações de fundo, não para um único fato concluído. Exemplo: "Da bambino giocavo sempre fuori" (Quando criança, eu sempre brincava fora), "Faceva bel tempo" (Fazia bom tempo).`],
    },
    'futuro-semplice': {
        it: [`Parlare del futuro`, `Si usa per parlare di progetti futuri, previsioni e promesse. Esempio: "Domani andrò al lavoro", "Sarà bellissimo".`],
        es: [`Hablar del futuro`, `Se usa para hablar de planes futuros, predicciones y promesas. Ejemplo: "Domani andrò al lavoro" (Mañana iré al trabajo), "Sarà bellissimo" (Será precioso).`],
        fr: [`Parler de l’avenir`, `Sert à parler de projets futurs, de prévisions et de promesses. Exemple : "Domani andrò al lavoro" (Demain j’irai au travail), "Sarà bellissimo" (Ce sera magnifique).`],
        pt: [`Falar do futuro`, `Usado para falar de planos futuros, previsões e promessas. Exemplo: "Domani andrò al lavoro" (Amanhã eu irei trabalhar), "Sarà bellissimo" (Será lindíssimo).`],
    },
    'condizionale': {
        it: [`Vorrei / potrei: richieste gentili`, `Si usa per esprimere richieste gentili, desideri ipotetici e affermazioni attenuate. Esempio: "Vorrei un caffè", "Potresti aiutarmi?".`],
        es: [`Querría / podría: peticiones educadas`, `Se usa para hacer peticiones educadas, expresar deseos hipotéticos y suavizar lo que se dice. Ejemplo: "Vorrei un caffè" (Querría un café), "Potresti aiutarmi?" (¿Podrías ayudarme?).`],
        fr: [`Je voudrais / je pourrais : demandes polies`, `Sert à formuler des demandes polies, des souhaits hypothétiques et à atténuer ce que l’on dit. Exemple : "Vorrei un caffè" (Je voudrais un café), "Potresti aiutarmi?" (Pourrais-tu m’aider ?).`],
        pt: [`Gostaria / poderia: pedidos educados`, `Usado para fazer pedidos educados, expressar desejos hipotéticos e suavizar o que se diz. Exemplo: "Vorrei un caffè" (Eu gostaria de um café), "Potresti aiutarmi?" (Você poderia me ajudar?).`],
    },
    'congiuntivo-presente': {
        it: [`Opinioni, dubbi, desideri`, `Si usa dopo espressioni di opinione, dubbio, desiderio o emozione, di solito dopo "che". Esempio: "Penso che sia vero", "Spero che tu stia bene".`],
        es: [`Opiniones, dudas, deseos`, `Se usa después de expresiones de opinión, duda, deseo o emoción, normalmente después de "che". Ejemplo: "Penso che sia vero" (Creo que es verdad), "Spero che tu stia bene" (Espero que estés bien).`],
        fr: [`Opinions, doutes, souhaits`, `S’emploie après des expressions d’opinion, de doute, de souhait ou d’émotion, généralement après "che". Exemple : "Penso che sia vero" (Je pense que c’est vrai), "Spero che tu stia bene" (J’espère que tu vas bien).`],
        pt: [`Opiniões, dúvidas, desejos`, `Usado depois de expressões de opinião, dúvida, desejo ou emoção, geralmente depois de "che". Exemplo: "Penso che sia vero" (Acho que é verdade), "Spero che tu stia bene" (Espero que você esteja bem).`],
    },
    'gerundio': {
        it: [`Azioni in corso: stare + gerundio`, `Si usa con "stare" per descrivere un’azione che sta accadendo adesso, come l’inglese "-ing". Esempio: "Sto mangiando", "Stavo dormendo".`],
        es: [`Acciones en curso: stare + gerundio`, `Se usa con "stare" para describir una acción que está ocurriendo ahora, como "estar + gerundio" en español. Ejemplo: "Sto mangiando" (Estoy comiendo), "Stavo dormendo" (Estaba durmiendo).`],
        fr: [`Actions en cours : stare + gérondif`, `S’emploie avec "stare" pour décrire une action en train de se dérouler, comme "être en train de" en français. Exemple : "Sto mangiando" (Je suis en train de manger), "Stavo dormendo" (J’étais en train de dormir).`],
        pt: [`Ações em andamento: stare + gerúndio`, `Usado com "stare" para descrever uma ação que está acontecendo agora, como "estar + gerúndio" em português. Exemplo: "Sto mangiando" (Estou comendo), "Stavo dormendo" (Eu estava dormindo).`],
    },
    'pronomi-diretti': {
        it: [`Pronomi complemento diretto: lo, la, li, le`, `Servono a sostituire un complemento oggetto già nominato, evitando ripetizioni. Esempio: "Vedi il libro? Lo vedo." — lo/la/li/le a seconda di genere e numero.`],
        es: [`Pronombres de objeto directo: lo, la, li, le`, `Sirven para sustituir un objeto directo ya mencionado y evitar repeticiones. Ejemplo: "Vedi il libro? Lo vedo." (¿Ves el libro? Lo veo.) — lo/la/li/le según el género y el número.`],
        fr: [`Pronoms COD : lo, la, li, le`, `Servent à remplacer un complément d’objet direct déjà mentionné pour éviter les répétitions. Exemple : "Vedi il libro? Lo vedo." (Tu vois le livre ? Je le vois.) — lo/la/li/le selon le genre et le nombre.`],
        pt: [`Pronomes de objeto direto: lo, la, li, le`, `Servem para substituir um objeto direto já mencionado, evitando repetições. Exemplo: "Vedi il libro? Lo vedo." (Você vê o livro? Eu o vejo.) — lo/la/li/le conforme o gênero e o número.`],
    },
    'pronomi-indiretti': {
        it: [`Pronomi complemento indiretto: gli, le, mi, ti`, `Servono a sostituire la persona a cui si dà o si dice qualcosa: mi, ti, gli, le, ci, vi, gli. Esempio: "Gli ho scritto una email", "Mi piace".`],
        es: [`Pronombres de objeto indirecto: gli, le, mi, ti`, `Sirven para sustituir a la persona a quien se da o se dice algo: mi, ti, gli, le, ci, vi, gli. Ejemplo: "Gli ho scritto una email" (Le escribí un correo), "Mi piace" (Me gusta).`],
        fr: [`Pronoms COI : gli, le, mi, ti`, `Servent à remplacer la personne à qui l’on donne ou dit quelque chose : mi, ti, gli, le, ci, vi, gli. Exemple : "Gli ho scritto una email" (Je lui ai écrit un e-mail), "Mi piace" (Ça me plaît).`],
        pt: [`Pronomes de objeto indireto: gli, le, mi, ti`, `Servem para substituir a pessoa a quem se dá ou se diz algo: mi, ti, gli, le, ci, vi, gli. Exemplo: "Gli ho scritto una email" (Eu escrevi um e-mail para ele), "Mi piace" (Eu gosto / Isso me agrada).`],
    },
    'preposizioni': {
        it: [`Preposizioni: a, di, da, in, con, su`, `Piccole parole che indicano il rapporto tra altre parole: luogo, direzione, possesso, tempo. Esempio: "Vado a Roma", "Il libro di Marco", "Sono di Milano".`],
        es: [`Preposiciones: a, di, da, in, con, su`, `Palabras pequeñas que indican la relación entre otras palabras: lugar, dirección, posesión, tiempo. Ejemplo: "Vado a Roma" (Voy a Roma), "Il libro di Marco" (El libro de Marco), "Sono di Milano" (Soy de Milán).`],
        fr: [`Prépositions : a, di, da, in, con, su`, `Petits mots qui indiquent le rapport entre d’autres mots : lieu, direction, possession, temps. Exemple : "Vado a Roma" (Je vais à Rome), "Il libro di Marco" (Le livre de Marco), "Sono di Milano" (Je viens de Milan).`],
        pt: [`Preposições: a, di, da, in, con, su`, `Palavras pequenas que indicam a relação entre outras palavras: lugar, direção, posse, tempo. Exemplo: "Vado a Roma" (Eu vou a Roma), "Il libro di Marco" (O livro do Marco), "Sono di Milano" (Eu sou de Milão).`],
    },
    'comparativi': {
        it: [`Fare confronti: più, meno, il più`, `Si usano per confrontare cose: "più/meno di" e "il più". Esempio: "È più alto di me", "È il più bravo della classe".`],
        es: [`Comparar cosas: più, meno, il più`, `Se usan para comparar cosas: "más/menos que" (più/meno di) y "el más" (il più). Ejemplo: "È più alto di me" (Es más alto que yo), "È il più bravo della classe" (Es el mejor de la clase).`],
        fr: [`Comparer : più, meno, il più`, `Servent à comparer des choses : "plus/moins que" (più/meno di) et "le plus" (il più). Exemple : "È più alto di me" (Il est plus grand que moi), "È il più bravo della classe" (C’est le meilleur de la classe).`],
        pt: [`Comparar coisas: più, meno, il più`, `Usados para comparar coisas: "mais/menos que" (più/meno di) e "o mais" (il più). Exemplo: "È più alto di me" (Ele é mais alto que eu), "È il più bravo della classe" (Ele é o melhor da turma).`],
    },
    'imperativo': {
        it: [`Dare ordini e istruzioni`, `Si usa per dare ordini, istruzioni o consigli in modo diretto. Esempio: "Vieni qui!", "Non parlare così!".`],
        es: [`Dar órdenes e instrucciones`, `Se usa para dar órdenes, instrucciones o consejos de forma directa. Ejemplo: "Vieni qui!" (¡Ven aquí!), "Non parlare così!" (¡No hables así!).`],
        fr: [`Donner des ordres et des instructions`, `S’emploie pour donner directement des ordres, des instructions ou des conseils. Exemple : "Vieni qui!" (Viens ici !), "Non parlare così!" (Ne parle pas comme ça !).`],
        pt: [`Dar ordens e instruções`, `Usado para dar ordens, instruções ou conselhos de forma direta. Exemplo: "Vieni qui!" (Venha aqui!), "Non parlare così!" (Não fale assim!).`],
    },
    'ci-ne': {
        it: [`Le piccole particelle difficili: ci e ne`, `"Ci" sostituisce un luogo o una cosa già nominati ("Vai al mare? Sì, ci vado"). "Ne" sostituisce "di esso/di loro" per le quantità ("Quanti ne vuoi? Ne voglio due").`],
        es: [`Las difíciles partículas ci y ne`, `"Ci" sustituye a un lugar o una cosa ya mencionados ("Vai al mare? Sì, ci vado" — ¿Vas al mar? Sí, voy). "Ne" sustituye a "de ello/de ellos" para cantidades ("Quanti ne vuoi? Ne voglio due" — ¿Cuántos quieres? Quiero dos).`],
        fr: [`Les petites particules délicates ci et ne`, `"Ci" remplace un lieu ou une chose déjà mentionnés ("Vai al mare? Sì, ci vado" — Tu vas à la mer ? Oui, j’y vais). "Ne" remplace "en" pour les quantités ("Quanti ne vuoi? Ne voglio due" — Combien en veux-tu ? J’en veux deux).`],
        pt: [`As difíceis partículas ci e ne`, `"Ci" substitui um lugar ou uma coisa já mencionados ("Vai al mare? Sì, ci vado" — Você vai ao mar? Sim, eu vou lá). "Ne" substitui "disso/deles" para quantidades ("Quanti ne vuoi? Ne voglio due" — Quantos você quer? Eu quero dois).`],
    },
    'si-impersonale': {
        it: [`Il "si" impersonale: si fa, la gente fa`, `Si usa per parlare di ciò che "la gente" o "si" fa in generale, senza dire chi: molto comune nell’italiano di tutti i giorni. Esempio: "Qui si mangia bene", "Come si dice...?", "Non si può fumare".`],
        es: [`El "si" impersonal: se hace, la gente hace`, `Se usa para hablar de lo que "la gente" o "uno" hace en general, sin decir quién; es muy común en el italiano cotidiano. Ejemplo: "Qui si mangia bene" (Aquí se come bien), "Come si dice...?" (¿Cómo se dice...?), "Non si può fumare" (No se puede fumar).`],
        fr: [`Le "si" impersonnel : on fait, les gens font`, `S’emploie pour dire ce que "les gens" ou "on" fait en général, sans dire qui ; très courant dans l’italien de tous les jours. Exemple : "Qui si mangia bene" (Ici, on mange bien), "Come si dice...?" (Comment dit-on… ?), "Non si può fumare" (On ne peut pas fumer).`],
        pt: [`O "si" impessoal: faz-se, as pessoas fazem`, `Usado para falar do que "as pessoas" ou "a gente" fazem em geral, sem dizer quem; é muito comum no italiano do dia a dia. Exemplo: "Qui si mangia bene" (Aqui se come bem), "Come si dice...?" (Como se diz...?), "Non si può fumare" (Não se pode fumar).`],
    },
    'past-simple': {
        it: [`Azioni concluse nel passato`, `Si usa per azioni iniziate e finite in un momento preciso del passato. Esempio: "I visited Rome last year" (Ho visitato Roma l’anno scorso), "She didn't call me yesterday" (Ieri non mi ha chiamato).`],
        es: [`Acciones terminadas en el pasado`, `Se usa para acciones que empezaron y terminaron en un momento concreto del pasado. Ejemplo: "I visited Rome last year" (Visité Roma el año pasado), "She didn't call me yesterday" (Ayer no me llamó).`],
        fr: [`Actions terminées dans le passé`, `Sert pour des actions commencées et terminées à un moment précis du passé. Exemple : "I visited Rome last year" (J’ai visité Rome l’an dernier), "She didn't call me yesterday" (Elle ne m’a pas appelé hier).`],
        pt: [`Ações concluídas no passado`, `Usado para ações que começaram e terminaram em um momento específico do passado. Exemplo: "I visited Rome last year" (Visitei Roma no ano passado), "She didn't call me yesterday" (Ela não me ligou ontem).`],
    },
    'present-perfect': {
        it: [`Azioni passate legate al presente`, `Si usa per esperienze, cambiamenti e situazioni che continuano fino a ora. Si forma con have/has + participio passato. Esempio: "I have lived here for five years" (Vivo qui da cinque anni), "Have you ever tried sushi?" (Hai mai provato il sushi?)`],
        es: [`Acciones pasadas conectadas con el presente`, `Se usa para experiencias, cambios y situaciones que continúan hasta ahora. Se forma con have/has + participio. Ejemplo: "I have lived here for five years" (Vivo aquí desde hace cinco años), "Have you ever tried sushi?" (¿Has probado alguna vez el sushi?)`],
        fr: [`Actions passées liées au présent`, `Sert pour des expériences, des changements et des situations qui durent jusqu’à maintenant. Se forme avec have/has + participe passé. Exemple : "I have lived here for five years" (J’habite ici depuis cinq ans), "Have you ever tried sushi?" (As-tu déjà goûté des sushis ?)`],
        pt: [`Ações passadas ligadas ao presente`, `Usado para experiências, mudanças e situações que continuam até agora. Forma-se com have/has + particípio passado. Exemplo: "I have lived here for five years" (Moro aqui há cinco anos), "Have you ever tried sushi?" (Você já experimentou sushi?)`],
    },
    'present-continuous': {
        it: [`Azioni che accadono adesso`, `Si usa per azioni che avvengono in questo momento o intorno a ora. Si forma con am/is/are + verbo-ing. Esempio: "I'm cooking dinner" (Sto cucinando la cena), "They're studying right now" (Stanno studiando proprio adesso).`],
        es: [`Acciones que ocurren ahora`, `Se usa para acciones que ocurren en este momento o alrededor de ahora. Se forma con am/is/are + verbo-ing. Ejemplo: "I'm cooking dinner" (Estoy preparando la cena), "They're studying right now" (Están estudiando ahora mismo).`],
        fr: [`Actions qui se déroulent maintenant`, `Sert pour des actions qui se déroulent en ce moment ou autour de maintenant. Se forme avec am/is/are + verbe-ing. Exemple : "I'm cooking dinner" (Je prépare le dîner), "They're studying right now" (Ils étudient en ce moment même).`],
        pt: [`Ações que acontecem agora`, `Usado para ações que acontecem neste momento ou por volta de agora. Forma-se com am/is/are + verbo-ing. Exemplo: "I'm cooking dinner" (Estou preparando o jantar), "They're studying right now" (Eles estão estudando agora mesmo).`],
    },
    'future-forms': {
        it: [`Will, going to e programmi`, `L’inglese ha vari modi per parlare del futuro. "Going to" serve per i progetti, "will" per le decisioni e le previsioni, e il present continuous per gli accordi già fissati. Esempio: "I'm going to visit my parents" (Andrò a trovare i miei genitori), "I'll help you with that" (Ti aiuto io), "I'm meeting Anna at six" (Vedo Anna alle sei).`],
        es: [`Will, going to y planes`, `El inglés tiene varias formas de hablar del futuro. "Going to" sirve para planes, "will" para decisiones y predicciones, y el present continuous para citas ya acordadas. Ejemplo: "I'm going to visit my parents" (Voy a visitar a mis padres), "I'll help you with that" (Yo te ayudo con eso), "I'm meeting Anna at six" (Quedo con Anna a las seis).`],
        fr: [`Will, going to et projets`, `L’anglais a plusieurs façons de parler du futur. "Going to" sert pour les projets, "will" pour les décisions et les prédictions, et le present continuous pour les rendez-vous déjà fixés. Exemple : "I'm going to visit my parents" (Je vais rendre visite à mes parents), "I'll help you with that" (Je vais t’aider), "I'm meeting Anna at six" (Je retrouve Anna à six heures).`],
        pt: [`Will, going to e planos`, `O inglês tem várias formas de falar do futuro. "Going to" serve para planos, "will" para decisões e previsões, e o present continuous para compromissos já marcados. Exemplo: "I'm going to visit my parents" (Vou visitar meus pais), "I'll help you with that" (Eu ajudo você com isso), "I'm meeting Anna at six" (Vou encontrar a Anna às seis).`],
    },
    'conditionals': {
        it: [`Se succede questo, allora...`, `Si usano per parlare di situazioni reali o immaginarie e dei loro risultati. Esempio: "If it rains, I'll stay home" (Se piove, resto a casa; reale), "If I had more time, I would learn Italian" (Se avessi più tempo, imparerei l’italiano; immaginario).`],
        es: [`Si pasa esto, entonces...`, `Se usan para hablar de situaciones reales o imaginarias y de sus resultados. Ejemplo: "If it rains, I'll stay home" (Si llueve, me quedo en casa; real), "If I had more time, I would learn Italian" (Si tuviera más tiempo, aprendería italiano; imaginario).`],
        fr: [`Si cela arrive, alors...`, `Servent à parler de situations réelles ou imaginaires et de leurs conséquences. Exemple : "If it rains, I'll stay home" (S’il pleut, je reste à la maison ; réel), "If I had more time, I would learn Italian" (Si j’avais plus de temps, j’apprendrais l’italien ; imaginaire).`],
        pt: [`Se isso acontecer, então...`, `Usados para falar de situações reais ou imaginárias e de seus resultados. Exemplo: "If it rains, I'll stay home" (Se chover, eu fico em casa; real), "If I had more time, I would learn Italian" (Se eu tivesse mais tempo, aprenderia italiano; imaginário).`],
    },
    'modal-verbs': {
        it: [`Can, should, must, might`, `Piccoli verbi ausiliari che esprimono capacità, consiglio, obbligo e possibilità. Esempio: "You should see a doctor" (Dovresti andare dal medico), "She might be late" (Potrebbe essere in ritardo), "I can't come tonight" (Stasera non posso venire).`],
        es: [`Can, should, must, might`, `Pequeños verbos auxiliares que expresan capacidad, consejo, obligación y posibilidad. Ejemplo: "You should see a doctor" (Deberías ir al médico), "She might be late" (Puede que llegue tarde), "I can't come tonight" (Esta noche no puedo venir).`],
        fr: [`Can, should, must, might`, `Petits verbes auxiliaires qui expriment la capacité, le conseil, l’obligation et la possibilité. Exemple : "You should see a doctor" (Tu devrais voir un médecin), "She might be late" (Elle pourrait être en retard), "I can't come tonight" (Je ne peux pas venir ce soir).`],
        pt: [`Can, should, must, might`, `Pequenos verbos auxiliares que expressam capacidade, conselho, obrigação e possibilidade. Exemplo: "You should see a doctor" (Você deveria ir ao médico), "She might be late" (Ela pode se atrasar), "I can't come tonight" (Hoje à noite eu não posso ir).`],
    },
    'phrasal-verbs': {
        it: [`Verbo + particella, come give up`, `Un verbo unito a una piccola parola che ne cambia il significato. Molto comuni nell’inglese di tutti i giorni. Esempio: "I gave up smoking" (Ho smesso di fumare), "Can you turn the music down?" (Puoi abbassare la musica?), "We ran out of milk" (Abbiamo finito il latte).`],
        es: [`Verbo + partícula, como give up`, `Un verbo unido a una palabra pequeña que cambia su significado. Muy comunes en el inglés cotidiano. Ejemplo: "I gave up smoking" (Dejé de fumar), "Can you turn the music down?" (¿Puedes bajar la música?), "We ran out of milk" (Se nos acabó la leche).`],
        fr: [`Verbe + particule, comme give up`, `Un verbe associé à un petit mot qui change son sens. Très courants dans l’anglais de tous les jours. Exemple : "I gave up smoking" (J’ai arrêté de fumer), "Can you turn the music down?" (Peux-tu baisser la musique ?), "We ran out of milk" (Nous n’avons plus de lait).`],
        pt: [`Verbo + partícula, como give up`, `Um verbo combinado com uma palavra pequena que muda seu significado. Muito comuns no inglês do dia a dia. Exemplo: "I gave up smoking" (Parei de fumar), "Can you turn the music down?" (Você pode abaixar a música?), "We ran out of milk" (O leite acabou).`],
    },
    'articles': {
        it: [`A, an e the`, `Si usa "a/an" per qualcosa di generico o nominato per la prima volta, e "the" per qualcosa di specifico o già noto. Esempio: "I saw a dog. The dog was huge." (Ho visto un cane. Il cane era enorme.), "She is an engineer" (Lei è ingegnere).`],
        es: [`A, an y the`, `Se usa "a/an" para algo general o mencionado por primera vez, y "the" para algo concreto o ya conocido. Ejemplo: "I saw a dog. The dog was huge." (Vi un perro. El perro era enorme.), "She is an engineer" (Ella es ingeniera).`],
        fr: [`A, an et the`, `On emploie "a/an" pour quelque chose de général ou mentionné pour la première fois, et "the" pour quelque chose de précis ou déjà connu. Exemple : "I saw a dog. The dog was huge." (J’ai vu un chien. Le chien était énorme.), "She is an engineer" (Elle est ingénieure).`],
        pt: [`A, an e the`, `Usa-se "a/an" para algo geral ou mencionado pela primeira vez, e "the" para algo específico ou já conhecido. Exemplo: "I saw a dog. The dog was huge." (Vi um cachorro. O cachorro era enorme.), "She is an engineer" (Ela é engenheira).`],
    },
    'comparatives': {
        it: [`Confrontare le cose`, `Si usano per confrontare cose: "more/less than" (più/meno di) e "the most" (il più). Esempio: "This is cheaper than that one" (Questo costa meno di quello), "It's the best film I've ever seen" (È il miglior film che abbia mai visto).`],
        es: [`Comparar cosas`, `Se usan para comparar cosas: "more/less than" (más/menos que) y "the most" (el más). Ejemplo: "This is cheaper than that one" (Este es más barato que aquel), "It's the best film I've ever seen" (Es la mejor película que he visto nunca).`],
        fr: [`Comparer des choses`, `Servent à comparer des choses : "more/less than" (plus/moins que) et "the most" (le plus). Exemple : "This is cheaper than that one" (Celui-ci est moins cher que celui-là), "It's the best film I've ever seen" (C’est le meilleur film que j’aie jamais vu).`],
        pt: [`Comparar coisas`, `Usados para comparar coisas: "more/less than" (mais/menos que) e "the most" (o mais). Exemplo: "This is cheaper than that one" (Este é mais barato que aquele), "It's the best film I've ever seen" (É o melhor filme que já vi).`],
    },
    'passive-voice': {
        it: [`Quando conta di più l’azione`, `Si usa quando l’azione o il risultato contano più di chi l’ha compiuta. Si forma con be + participio passato. Esempio: "The bridge was built in 1920" (Il ponte fu costruito nel 1920), "English is spoken all over the world" (L’inglese si parla in tutto il mondo).`],
        es: [`Cuando lo importante es la acción`, `Se usa cuando la acción o el resultado importan más que quién la realizó. Se forma con be + participio. Ejemplo: "The bridge was built in 1920" (El puente fue construido en 1920), "English is spoken all over the world" (El inglés se habla en todo el mundo).`],
        fr: [`Quand l’action compte le plus`, `S’emploie quand l’action ou le résultat comptent plus que la personne qui l’a faite. Se forme avec be + participe passé. Exemple : "The bridge was built in 1920" (Le pont a été construit en 1920), "English is spoken all over the world" (L’anglais est parlé dans le monde entier).`],
        pt: [`Quando a ação é o mais importante`, `Usada quando a ação ou o resultado importam mais do que quem a realizou. Forma-se com be + particípio passado. Exemplo: "The bridge was built in 1920" (A ponte foi construída em 1920), "English is spoken all over the world" (O inglês é falado no mundo inteiro).`],
    },
    'reported-speech': {
        it: [`Riferire ciò che ha detto qualcuno`, `Si usa per riferire ciò che qualcuno ha detto senza citarlo alla lettera. Il tempo verbale di solito fa un passo indietro. Esempio: "She said she was tired" (Ha detto di essere stanca), "He asked me where I lived" (Mi ha chiesto dove abitavo).`],
        es: [`Contar lo que alguien dijo`, `Se usa para contar lo que alguien dijo sin citarlo literalmente. El tiempo verbal suele retroceder un paso. Ejemplo: "She said she was tired" (Dijo que estaba cansada), "He asked me where I lived" (Me preguntó dónde vivía).`],
        fr: [`Rapporter ce que quelqu’un a dit`, `Sert à rapporter ce que quelqu’un a dit sans le citer mot pour mot. Le temps verbal recule généralement d’un cran. Exemple : "She said she was tired" (Elle a dit qu’elle était fatiguée), "He asked me where I lived" (Il m’a demandé où j’habitais).`],
        pt: [`Contar o que alguém disse`, `Usado para contar o que alguém disse sem citá-lo literalmente. O tempo verbal geralmente recua um passo. Exemplo: "She said she was tired" (Ela disse que estava cansada), "He asked me where I lived" (Ele me perguntou onde eu morava).`],
    },
};

export default function Luna() {
    const navigate = useNavigate();
    const { topicSlug } = useParams();
    const topicName = (topicSlug || 'everyday-life').replace(/-/g, ' ');

    const [sessionMode, setSessionMode] = useState(null);
    const [showScenarioPicker, setShowScenarioPicker] = useState(false);
    const [chosenScenario, setChosenScenario] = useState(null);
    const [showGrammarPicker, setShowGrammarPicker] = useState(false);
    const [chosenGrammar, setChosenGrammar] = useState(null);
    const [customGrammarInput, setCustomGrammarInput] = useState('');
    const [previewGrammar, setPreviewGrammar] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [viewingHistoryItem, setViewingHistoryItem] = useState(null);
    const sessionStartRef = useRef(null);
    const sessionIdRef = useRef(null);
    const [correctionStyle, setCorrectionStyle] = useState('gentle');
    const [voiceSpeed, setVoiceSpeed] = useState(() => {
        const lvl = localStorage.getItem('vozoly_level') || 'A1';
        return lvl === 'A1' ? 0.8 : lvl === 'A2' ? 0.9 : 1.0;
    });
    const voiceSpeedRef = useRef(voiceSpeed);
    const [messages, setMessages] = useState([]);
    const [lunaThinking, setLunaThinking] = useState(false);
    const [listening, setListening] = useState(false);
    const [textMode, setTextMode] = useState(false);
    const [textInput, setTextInput] = useState('');
    const [explainFor, setExplainFor] = useState(null);
    const [explanation, setExplanation] = useState('');
    const [explaining, setExplaining] = useState(false);
    const [saveWord, setSaveWord] = useState(null);
    const [wordSaved, setWordSaved] = useState(false);
    const [saveChecking, setSaveChecking] = useState(false);
    const [saveDuplicate, setSaveDuplicate] = useState(null);
    const [saveRelated, setSaveRelated] = useState(null);
    const [translations, setTranslations] = useState({});
    const [translatingIdx, setTranslatingIdx] = useState(null);
    const [speechSupported, setSpeechSupported] = useState(true);
    const messagesEndRef = useRef(null);
    const holdingRef = useRef(false);
    const lockedRef = useRef(false);
    const touchStartXRef = useRef(0);
    const [locked, setLocked] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [recordSeconds, setRecordSeconds] = useState(0);
    const mediaRecorderRef = useRef(null);
    const lunaAudioRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const recordTimerRef = useRef(null);
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const languageName = languageNameMap[targetLang] || 'Italian';
    const strings = lunaStrings[targetLang] || lunaStrings.italian;
    const grammarTopics = grammarTopicsByLanguage[targetLang] || [];

    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    // Topic name shown on screen, in the interface language (the plain English name is still what Gabi's prompt uses)
    const topicDisplayName = (() => {
        const found = getTopicBySlug(topicSlug);
        if (found) return getTopicName(found, uiLang);
        const everydayLife = { en: 'Everyday life', it: 'Vita quotidiana', es: 'Vida cotidiana', fr: 'Vie quotidienne', pt: 'Vida cotidiana' };
        return everydayLife[uiLang] || everydayLife.en;
    })();
    const tx = (key, values) => {
        let out = TEXT[uiLang][key] ?? TEXT.en[key] ?? key;
        if (values) {
            Object.keys(values).forEach(k => { out = out.split(`{${k}}`).join(values[k]); });
        }
        return out;
    };
    const langLabel = LANG_LABEL[uiLang][targetLang] || languageName;
    // Gabi's own error messages are in the language being learned; add a hint in the interface language when it differs
    const withHint = (message, hintKey) => (UI_TO_LANG[uiLang] === targetLang ? message : `${message} (${tx(hintKey)})`);
    const grammarInfo = (g) => {
        const t = (GRAMMAR_TEXT[g.id] || EXTRA_GRAMMAR_TEXT[g.id])?.[uiLang];
        return t ? { desc: t[0], explanation: t[1] } : { desc: g.desc, explanation: g.explanation };
    };
    const scenarioTexts = (sc) => {
        const t = SCENARIO_TEXT[sc.id]?.[uiLang];
        return t ? { name: t[0], desc: t[1] } : { name: sc.name, desc: sc.desc };
    };
    // Saved conversation labels are stored in English; show them in the interface language
    const localizeLabel = (label) => {
        if (label === 'Free conversation') return tx('freeName');
        if (typeof label === 'string' && label.startsWith('Grammar: ')) return `${tx('grammarLabel')}: ${label.slice(9)}`;
        const sc = rolePlayScenarios.find(x => x.name === label);
        return sc ? scenarioTexts(sc).name : label;
    };

    useEffect(() => {
        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setSpeechSupported(false);
            setTextMode(true);
        }
        return () => {
            try { mediaRecorderRef.current?.stop(); } catch (e) { }
            streamRef.current?.getTracks().forEach(t => t.stop());
            clearInterval(recordTimerRef.current);
            lunaAudioRef.current?.pause();
            stopSpeaking();
        };
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, lunaThinking]);

    const speakLuna = async (text) => {
        try {
            lunaAudioRef.current?.pause();
            const response = await fetch('/api/tts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: text.replace(/[\u00A0\u202F]/g, ' ').replace(/\s+([?!:;»])/g, '$1').replace(/«\s*/g, ''), language: targetLang }),
            });
            if (!response.ok) throw new Error('tts failed');
            const { audio } = await response.json();
            const player = new Audio(`data:audio/mpeg;base64,${audio}`);
            player.playbackRate = voiceSpeedRef.current;
            lunaAudioRef.current = player;
            player.play().catch(() => { });
        } catch (err) {
            speakText(text, undefined, targetLang);
        }
    };

    const stopLuna = () => {
        lunaAudioRef.current?.pause();
        stopSpeaking();
    };

    const changeSpeed = () => {
        const speeds = [0.7, 0.8, 0.9, 1.0];
        const next = speeds[(speeds.indexOf(voiceSpeedRef.current) + 1) % speeds.length];
        voiceSpeedRef.current = next;
        setVoiceSpeed(next);
        if (lunaAudioRef.current && !lunaAudioRef.current.paused) {
            lunaAudioRef.current.playbackRate = next;
        }
    };

    const saveConversationSnapshot = async (allMessages) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || allMessages.length < 2) return;
        if (!sessionIdRef.current) sessionIdRef.current = crypto.randomUUID();

        const label = chosenScenario ? chosenScenario.name
            : chosenGrammar ? `Grammar: ${chosenGrammar}`
                : `Free conversation`;

        await supabase.from('luna_conversations').upsert({
            id: sessionIdRef.current,
            user_id: user.id,
            session_mode: sessionMode || 'chat',
            session_label: label,
            correction_style: correctionStyle,
            messages: allMessages,
            updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

        const { data: all } = await supabase
            .from('luna_conversations')
            .select('id')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false });
        if (all && all.length > 2) {
            const toDelete = all.slice(2).map(r => r.id);
            await supabase.from('luna_conversations').delete().in('id', toDelete);
        }
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setHistoryLoading(false); return; }
        const { data } = await supabase
            .from('luna_conversations')
            .select('*')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false })
            .limit(2);
        setHistoryList(data || []);
        setHistoryLoading(false);
    };

    const startSession = async (mode, scenario = null, grammarTopicName = null) => {
        setSessionMode(mode);
        setChosenScenario(scenario);
        setChosenGrammar(grammarTopicName);
        setShowScenarioPicker(false);
        setShowGrammarPicker(false);
        sessionStartRef.current = Date.now();
        sessionIdRef.current = null;
        const displayName = localStorage.getItem('vozoly_display_name') || '';
        const namePart = displayName ? ' ' + displayName : '';
        const topicPart = topicName !== 'everyday life' ? ' di ' + topicName : '';
        let greeting;
        if (mode === 'grammar' && grammarTopicName) {
            // Grammar practice: Gabi opens by introducing the chosen grammar
            setMessages([]);
            setLunaThinking(true);
            try {
                const openerRes = await fetch('/api/luna-chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        messages: [{ role: 'user', content: 'Start the conversation.' }],
                        level,
                        language: languageName,
                        mode: 'opener',
                        grammarTopic: grammarTopicName,
                    }),
                });
                const openerData = await openerRes.json();
                greeting = openerData.reply || '';
            } catch (err) {
                greeting = '';
            }
            setLunaThinking(false);
            if (!greeting) {
                const grammarFallbacks = {
                    italian: `Oggi esercitiamo: ${grammarTopicName}. Cominciamo! Dimmi una frase usando questo argomento.`,
                    english: `Today we're practising: ${grammarTopicName}. Let's start! Tell me a sentence using it.`,
                    spanish: `Hoy practicamos: ${grammarTopicName}. ¡Empecemos! Dime una frase usando este tema.`,
                    french: `Aujourd'hui, on travaille : ${grammarTopicName}. Commençons ! Dis-moi une phrase avec ce point de grammaire.`,
                    portuguese: `Hoje vamos praticar: ${grammarTopicName}. Vamos começar! Diga uma frase usando este tópico.`,
                };
                greeting = grammarFallbacks[targetLang] || grammarFallbacks.english;
            }
        } else if (mode === 'scenario' && scenario) {
            if (targetLang === 'italian') {
                greeting = scenario.opener;
            } else {
                // Other languages: Gabi writes the opening line herself
                setMessages([]);
                setLunaThinking(true);
                try {
                    const openerRes = await fetch('/api/luna-chat', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            messages: [{ role: 'user', content: 'Start the conversation.' }],
                            level,
                            language: languageName,
                            mode: 'opener',
                            scenario: localizeScenarioPrompt(scenario.prompt, targetLang) + buildRegisterNote(scenario.register, targetLang),
                        }),
                    });
                    const openerData = await openerRes.json();
                    greeting = openerData.reply || '';
                } catch (err) {
                    greeting = '';
                }
                setLunaThinking(false);
                if (!greeting) greeting = (otherGreetings[targetLang] || otherGreetings.english)(namePart)[0];
            }
        } else {
            const options = targetLang === 'italian'
                ? freeGreetings(namePart, topicPart)
                : (otherGreetings[targetLang] || otherGreetings.english)(namePart);
            greeting = options[Math.floor(Math.random() * options.length)];
        }
        setMessages([{ role: 'assistant', content: greeting }]);
        speakLuna(greeting);
    };

    const sendMessage = async (text) => {
        const clean = text.trim();
        if (!clean || lunaThinking) return;
        setTextInput('');

        const newMessages = [...messages, { role: 'user', content: clean }];
        setMessages(newMessages);
        setLunaThinking(true);

        try {
            const italianScenarioPrompt = sessionMode === 'scenario' && chosenScenario
                ? `${chosenScenario.prompt} REGISTER: This situation requires the ${chosenScenario.register === 'formal' ? 'FORMAL register (Lei). The student should address you with "Lei" and formal verb forms. If they use informal "tu" forms with you, treat it as an error worth correcting (per your correction style) — politely note that in this situation Italians use the formal Lei' : 'INFORMAL register (tu). The student should address you with "tu". If they use overly formal "Lei" forms, gently note (per your correction style) that with friends this sounds too distant — "dammi del tu!"'}. You yourself must consistently model the correct register when speaking to the student.`
                : null;
            const scenario = !(sessionMode === 'scenario' && chosenScenario)
                ? null
                : targetLang === 'italian'
                    ? italianScenarioPrompt
                    : localizeScenarioPrompt(chosenScenario.prompt, targetLang) + buildRegisterNote(chosenScenario.register, targetLang);
            const elapsedMinutes = sessionStartRef.current
                ? Math.floor((Date.now() - sessionStartRef.current) / 60000)
                : 0;

            const response = await fetch('/api/luna-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: newMessages,
                    topicName,
                    level,
                    language: languageName,
                    interfaceLanguage,
                    mode: 'chat',
                    scenario,
                    correctionStyle,
                    elapsedMinutes,
                    grammarTopic: sessionMode === 'grammar' ? chosenGrammar : null,
                }),
            });
            if (!response.ok) throw new Error('unavailable');
            const data = await response.json();
            const updatedMessages = [...newMessages, { role: 'assistant', content: data.reply }];
            setMessages(updatedMessages);
            speakLuna(data.reply);
            saveConversationSnapshot(updatedMessages);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: withHint(strings.trouble, 'errTrouble') }]);
        }
        setLunaThinking(false);
    };

    const handleMicDown = async (clientX) => {
        if (lunaThinking || transcribing) return;
        window.speechSynthesis?.cancel();
        lunaAudioRef.current?.pause();
        touchStartXRef.current = clientX || 0;
        lockedRef.current = false;
        setLocked(false);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            audioChunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            holdingRef.current = true;
            setListening(true);
            setRecordSeconds(0);
            recordTimerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
        } catch (err) {
            setSpeechSupported(false);
            setTextMode(true);
        }
    };

    const handleTouchMove = (e) => {
        if (!holdingRef.current || lockedRef.current) return;
        const currentX = e.touches?.[0]?.clientX || 0;
        if (touchStartXRef.current - currentX > 60) {
            lockedRef.current = true;
            setLocked(true);
        }
    };

    const finishRecording = () => {
        holdingRef.current = false;
        lockedRef.current = false;
        setLocked(false);
        setListening(false);
        clearInterval(recordTimerRef.current);
        return new Promise((resolve) => {
            const recorder = mediaRecorderRef.current;
            if (!recorder || recorder.state === 'inactive') { resolve(null); return; }
            recorder.onstop = () => {
                streamRef.current?.getTracks().forEach(t => t.stop());
                const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType });
                resolve(blob.size > 1000 ? blob : null);
            };
            recorder.stop();
        });
    };

    const stopAndSend = async () => {
        const blob = await finishRecording();
        if (!blob) return;
        setTranscribing(true);
        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            const createRes = await fetch('/api/transcribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audio: base64, language: targetLang }),
            });
            if (!createRes.ok) throw new Error('create failed');
            const { id } = await createRes.json();

            let text = '';
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1200));
                const pollRes = await fetch(`/api/transcribe?id=${id}`);
                const poll = await pollRes.json();
                if (poll.status === 'completed') { text = poll.text; break; }
                if (poll.status === 'error') break;
            }

            setTranscribing(false);
            if (text.trim()) {
                sendMessage(text);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', content: withHint(strings.didntHear, 'errDidntHear') }]);
            }
        } catch (err) {
            setTranscribing(false);
            setMessages(prev => [...prev, { role: 'assistant', content: withHint(strings.audioFailed, 'errAudio') }]);
        }
    };

    const cancelRecording = async () => {
        await finishRecording();
        audioChunksRef.current = [];
    };

    const handleMicUp = () => {
        if (lockedRef.current) return;
        if (!holdingRef.current) return;
        stopAndSend();
    };

    const handleWordTap = (rawWord) => {
        const cleaned = rawWord.replace(/[.,!?;:"""''()]/g, '').trim().toLowerCase();
        if (cleaned.length < 2) return;
        setSaveWord(cleaned);
        setWordSaved(false);
        setSaveChecking(false);
        setSaveDuplicate(null);
        setSaveRelated(null);
    };

    const saveWordToBank = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !saveWord) return;
        setSaveChecking(true);

        const { data: existing } = await supabase
            .from('word_bank')
            .select('word')
            .eq('user_id', user.id)
            .eq('target_language', targetLang);
        const userWords = (existing || []).map(w => w.word.toLowerCase());

        if (userWords.includes(saveWord)) {
            setSaveChecking(false);
            setSaveDuplicate(saveWord);
            return;
        }

        let result = null;
        try {
            const response = await fetch('/api/identify-word', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ word: saveWord, language: languageName, interfaceLanguage }),
            });
            if (response.ok) result = await response.json();
        } catch (err) { }

        if (result && result.valid) {
            const baseForm = result.wordToSave?.toLowerCase();
            const relatedMatch = userWords.find(w =>
                w === baseForm ||
                (baseForm && w.startsWith(baseForm.slice(0, Math.max(4, baseForm.length - 3))))
            );
            if (relatedMatch && relatedMatch !== saveWord) {
                setSaveChecking(false);
                setSaveRelated({ existing: relatedMatch, result });
                return;
            }
        }

        await doSave(user, result);
    };

    const doSave = async (user, result) => {
        let note = result?.note || '';
        if (result?.isConjugated) {
            note = `${result.conjugationInfo} form of "${result.baseForm}". ${note}`.trim();
        }
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: saveWord,
            translation: result?.translation || null,
            phonetic: result?.phonetic || null,
            type: result?.type || null,
            note,
            examples: result?.examples || [],
            entry_type: 'word',
            source: 'luna',
            target_language: targetLang,
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: false });
        setSaveChecking(false);
        setSaveRelated(null);
        setWordSaved(true);
    };

    const confirmSaveAnyway = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        setSaveChecking(true);
        await doSave(user, saveRelated?.result || null);
    };

    const translateMessage = async (idx) => {
        if (translations[idx]) {
            setTranslations(prev => ({ ...prev, [idx]: undefined }));
            return;
        }
        setTranslatingIdx(idx);
        try {
            const response = await fetch('/api/luna-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: [{ role: 'user', content: messages[idx].content }],
                    language: languageName,
                    interfaceLanguage,
                    mode: 'translate',
                }),
            });
            if (!response.ok) throw new Error('failed');
            const data = await response.json();
            setTranslations(prev => ({ ...prev, [idx]: data.reply }));
        } catch (err) {
            setTranslations(prev => ({ ...prev, [idx]: tx('translateFail') }));
        }
        setTranslatingIdx(null);
    };

    const requestExplanation = async (messageIndex) => {
        const userMessage = messages[messageIndex];
        if (!userMessage || userMessage.role !== 'user') return;
        setExplainFor(messageIndex);
        setExplanation('');
        setExplaining(true);
        try {
            const response = await fetch('/api/luna-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: [{ role: 'user', content: `My message in ${languageName} was: "${userMessage.content}"\n\nContext — the previous message from my conversation partner was: "${messages[messageIndex - 1]?.content || 'none'}"` }],
                    level,
                    language: languageName,
                    interfaceLanguage,
                    mode: 'explain',
                }),
            });
            if (!response.ok) throw new Error('unavailable');
            const data = await response.json();
            setExplanation(data.reply);
        } catch (err) {
            setExplanation(tx('feedbackFail'));
        }
        setExplaining(false);
    };

    if (!sessionMode && showGrammarPicker) {
        return (
            <div className="luna-screen">
                <div className="luna-top-bar">
                    <button className="luna-close" onClick={() => setShowGrammarPicker(false)}>←</button>
                    <div className="luna-title">{tx('chooseGrammar')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="luna-scenario-list">
                    <div className="luna-custom-grammar">
                        <div className="luna-correction-label">{tx('ownTopic')}</div>
                        <div className="luna-custom-grammar-row">
                            <input
                                className="luna-text-input"
                                type="text"
                                placeholder={targetLang === 'italian' ? `${tx('eg')} periodo ipotetico, passato remoto...` : `${tx('eg')} ${tx('grammarPlaceholder')}`}
                                value={customGrammarInput}
                                onChange={e => setCustomGrammarInput(e.target.value)}
                            />
                            <button
                                className="luna-send-btn"
                                disabled={!customGrammarInput.trim()}
                                onClick={() => startSession('grammar', null, customGrammarInput.trim())}
                            >
                                →
                            </button>
                        </div>
                    </div>

                    {grammarTopics.length > 0 && <div className="luna-correction-label" style={{ marginTop: 8 }}>{tx('pickCommon')}</div>}
                    {grammarTopics.map(g => (
                        <button key={g.id} className="luna-mode-card" onClick={() => setPreviewGrammar(g)}>
                            <div className="luna-mode-emoji">📐</div>
                            <div>
                                <div className="luna-mode-name">{g.name}</div>
                                <div className="luna-mode-desc">{grammarInfo(g).desc}</div>
                            </div>
                        </button>
                    ))}
                </div>

                {previewGrammar && (
                    <div className="luna-explain-overlay" onClick={() => setPreviewGrammar(null)}>
                        <div className="luna-explain-sheet" onClick={e => e.stopPropagation()}>
                            <div className="luna-save-word-title" style={{ fontSize: 20 }}>{previewGrammar.name}</div>
                            <div className="luna-explain-title" style={{ marginTop: 4 }}>{tx('howWhen')}</div>
                            <div className="luna-explain-body">{grammarInfo(previewGrammar).explanation}</div>
                            <button className="luna-save-btn" onClick={() => startSession('grammar', null, previewGrammar.name)}>
                                {tx('startPractice')}
                            </button>
                            <button className="luna-explain-close" style={{ background: 'transparent', color: '#6B7280' }} onClick={() => setPreviewGrammar(null)}>{tx('chooseAnother')}</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (showHistory) {
        return (
            <div className="luna-screen">
                <div className="luna-top-bar">
                    <button className="luna-close" onClick={() => setShowHistory(false)}>←</button>
                    <div className="luna-title">{tx('pastTitle')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="luna-scenario-list">
                    {historyLoading ? (
                        <div className="luna-history-empty">{tx('loading')}</div>
                    ) : historyList.length === 0 ? (
                        <div className="luna-history-empty">{tx('noHistory')}</div>
                    ) : (
                        historyList.map(item => (
                            <button key={item.id} className="luna-mode-card" onClick={() => setViewingHistoryItem(item)}>
                                <div className="luna-mode-emoji">💬</div>
                                <div>
                                    <div className="luna-mode-name">{localizeLabel(item.session_label)}</div>
                                    <div className="luna-mode-desc">
                                        {new Date(item.updated_at).toLocaleDateString(DATE_LOCALES[uiLang], { day: 'numeric', month: 'short' })} · {tx('messagesCount', { n: item.messages.length })}
                                    </div>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {viewingHistoryItem && (
                    <div className="luna-explain-overlay" onClick={() => setViewingHistoryItem(null)}>
                        <div className="luna-history-transcript" onClick={e => e.stopPropagation()}>
                            <div className="luna-explain-title">{localizeLabel(viewingHistoryItem.session_label)}</div>
                            <div className="luna-history-scroll">
                                {viewingHistoryItem.messages.map((m, i) => (
                                    <div key={i} className={`luna-msg-row ${m.role}`} style={{ marginBottom: 8 }}>
                                        <div className={`luna-msg ${m.role}`}>{m.content}</div>
                                    </div>
                                ))}
                            </div>
                            <button className="luna-explain-close" onClick={() => setViewingHistoryItem(null)}>{tx('close')}</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (!sessionMode && showScenarioPicker) {
        return (
            <div className="luna-screen">
                <div className="luna-top-bar">
                    <button className="luna-close" onClick={() => setShowScenarioPicker(false)}>←</button>
                    <div className="luna-title">{tx('chooseScenario')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="luna-scenario-list">
                    {rolePlayScenarios.map(s => (
                        <button key={s.id} className="luna-mode-card" onClick={() => startSession('scenario', s)}>
                            <div className="luna-mode-emoji">{s.emoji}</div>
                            <div style={{ flex: 1 }}>
                                <div className="luna-mode-name">{scenarioTexts(s).name}</div>
                                <div className="luna-mode-desc">{scenarioTexts(s).desc}</div>
                            </div>
                            {hasFormalRegister[targetLang] && (
                                <div className={`luna-register-tag ${s.register}`}>
                                    {s.register === 'formal' ? formalYouLabels[targetLang] : informalYouLabels[targetLang]}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (!sessionMode) {
        return (
            <div className="luna-screen">
                <div className="luna-top-bar">
                    <button className="luna-close" onClick={() => { stopLuna(); navigate(-1); }}>✕</button>
                    <div className="luna-title">Gabi</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="luna-mode-picker">
                    <div className="luna-avatar-large">🦉</div>
                    <div className="luna-mode-title">{tx('practiceWith')}</div>
                    <div className="luna-mode-sub">{tx('topicLabel', { topic: topicDisplayName })}</div>
                    <button className="luna-mode-card" onClick={() => startSession('chat')}>
                        <div className="luna-mode-emoji">💬</div>
                        <div>
                            <div className="luna-mode-name">{tx('freeName')}</div>
                            <div className="luna-mode-desc">{tx('freeDesc')}</div>
                        </div>
                    </button>
                    <button className="luna-mode-card" onClick={() => setShowScenarioPicker(true)}>
                        <div className="luna-mode-emoji">🎭</div>
                        <div>
                            <div className="luna-mode-name">{tx('roleName')}</div>
                            <div className="luna-mode-desc">{tx('roleDesc')}</div>
                        </div>
                    </button>
                    <button className="luna-mode-card" onClick={() => setShowGrammarPicker(true)}>
                        <div className="luna-mode-emoji">📐</div>
                        <div>
                            <div className="luna-mode-name">{tx('grammarName')}</div>
                            <div className="luna-mode-desc">{tx('grammarDesc')}</div>
                        </div>
                    </button>

                    <div className="luna-correction-label">{tx('correctLabel')}</div>
                    <div className="luna-correction-row">
                        <button
                            className={`luna-correction-btn ${correctionStyle === 'gentle' ? 'active' : ''}`}
                            onClick={() => setCorrectionStyle('gentle')}
                        >
                            <div className="luna-correction-emoji">🌱</div>
                            <div className="luna-correction-name">{tx('gentle')}</div>
                            <div className="luna-correction-desc">{tx('gentleDesc')}</div>
                        </button>
                        <button
                            className={`luna-correction-btn ${correctionStyle === 'strict' ? 'active' : ''}`}
                            onClick={() => setCorrectionStyle('strict')}
                        >
                            <div className="luna-correction-emoji">🎯</div>
                            <div className="luna-correction-name">{tx('strict')}</div>
                            <div className="luna-correction-desc">{tx('strictDesc')}</div>
                        </button>
                    </div>

                    <button className="luna-history-link" onClick={() => { setShowHistory(true); loadHistory(); }}>
                        {tx('history')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="luna-screen">
            <div className="luna-top-bar">
                <button className="luna-close" onClick={() => navigate(-1)}>✕</button>
                <div className="luna-title-wrap">
                    <div className="luna-title">Gabi</div>
                    <div className="luna-mode-badge">
                        {chosenGrammar ? `📐 ${chosenGrammar}` : correctionStyle === 'strict' ? `🎯 ${tx('strict')}` : `🌱 ${tx('gentle')}`}
                    </div>
                </div>
                <div className="luna-top-right">
                    <button className="luna-speed-btn" onClick={changeSpeed} title={tx('voiceSpeed')}>
                        {voiceSpeed}x
                    </button>
                    <div className="luna-avatar-small">🦉</div>
                </div>
            </div>

            <div className="luna-messages">
                {messages.map((m, i) => (
                    <div key={i} className={`luna-msg-row ${m.role}`}>
                        <div className={`luna-msg ${m.role}`}>
                            {m.role === 'assistant' ? (
                                <span>
                                    {m.content.split(/(\s+)/).map((part, j) =>
                                        /\S/.test(part) ? (
                                            <span key={j} className="luna-word" onClick={() => handleWordTap(part)}>{part}</span>
                                        ) : (
                                            <span key={j}>{part}</span>
                                        )
                                    )}
                                </span>
                            ) : (
                                m.content
                            )}
                            {m.role === 'assistant' && (
                                <span className="luna-msg-actions">
                                    <button className="luna-msg-speak" onClick={() => stopLuna()}>⏹</button>
                                    <button className="luna-msg-speak" onClick={() => speakLuna(m.content)}>🔊</button>
                                    <button className="luna-msg-translate" onClick={() => translateMessage(i)}>
                                        {translatingIdx === i ? '...' : '文'}
                                    </button>
                                </span>
                            )}
                            {m.role === 'user' && (
                                <button className="luna-msg-explain" onClick={() => requestExplanation(i)} title={tx('checkMy', { language: langLabel })}>?</button>
                            )}
                            {translations[i] && (
                                <div className="luna-translation">{translations[i]}</div>
                            )}
                        </div>
                    </div>
                ))}
                {lunaThinking && (
                    <div className="luna-msg-row assistant">
                        <div className="luna-msg assistant luna-typing">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef}></div>
            </div>

            {transcribing && (
                <div className="luna-interim">✍️ {tx('transcribingYour', { language: langLabel })}</div>
            )}

            <div className="luna-input-bar">
                {textMode ? (
                    <>
                        <input
                            className="luna-text-input"
                            type="text"
                            placeholder={strings.placeholder}
                            value={textInput}
                            onChange={e => setTextInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendMessage(textInput)}
                        />
                        <button className="luna-send-btn" onClick={() => sendMessage(textInput)} disabled={!textInput.trim() || lunaThinking}>→</button>
                        {speechSupported && (
                            <button className="luna-toggle-btn" onClick={() => setTextMode(false)}>🎤</button>
                        )}
                    </>
                ) : (
                    <>
                        <button className="luna-toggle-btn" onClick={() => setTextMode(true)}>⌨️</button>
                        {locked ? (
                            <div className="luna-locked-controls">
                                <button className="luna-cancel-btn" onClick={cancelRecording}>✕</button>
                                <div className="luna-locked-indicator">🔴 {Math.floor(recordSeconds / 60)}:{String(recordSeconds % 60).padStart(2, '0')}</div>
                                <button className="luna-locked-send-btn" onClick={stopAndSend}>➤</button>
                            </div>
                        ) : (
                            <>
                                <button
                                    className={`luna-mic-btn ${listening ? 'listening' : ''}`}
                                    onMouseDown={(e) => handleMicDown(e.clientX)}
                                    onMouseUp={handleMicUp}
                                    onTouchStart={(e) => { e.preventDefault(); handleMicDown(e.touches[0].clientX); }}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={(e) => { e.preventDefault(); handleMicUp(); }}
                                    disabled={lunaThinking}
                                >
                                    {listening ? '🔴' : '🎤'}
                                </button>
                                <div className="luna-mic-hint">
                                    {transcribing ? tx('transcribing') : listening ? `${Math.floor(recordSeconds / 60)}:${String(recordSeconds % 60).padStart(2, '0')} · ${tx('slideLock')}` : tx('holdToSpeak')}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {saveWord && (
                <div className="luna-explain-overlay" onClick={() => setSaveWord(null)}>
                    <div className="luna-explain-sheet" onClick={e => e.stopPropagation()}>
                        <div className="luna-save-word-title">{saveWord}</div>
                        <button className="luna-save-hear" onClick={() => speakLuna(saveWord)}>{tx('hearIt')}</button>
                        {wordSaved ? (
                            <div className="luna-save-done">{tx('saved')}</div>
                        ) : saveDuplicate ? (
                            <div className="luna-save-dup">{tx('alreadyIn', { word: saveDuplicate })}</div>
                        ) : saveRelated ? (
                            <>
                                <div className="luna-save-dup">
                                    {tx('relatedBefore')}<strong>"{saveRelated.existing}"</strong>{tx('relatedAfter', { word: saveWord })}
                                </div>
                                <button className="luna-save-btn" onClick={confirmSaveAnyway} disabled={saveChecking}>
                                    {saveChecking ? tx('saving') : tx('saveAnyway', { word: saveWord })}
                                </button>
                            </>
                        ) : (
                            <button className="luna-save-btn" onClick={saveWordToBank} disabled={saveChecking}>
                                {saveChecking ? tx('checking') : tx('saveToBank')}
                            </button>
                        )}
                        <button className="luna-explain-close" style={{ background: 'transparent', color: '#6B7280' }} onClick={() => setSaveWord(null)}>{tx('close')}</button>
                    </div>
                </div>
            )}

            {explainFor !== null && (
                <div className="luna-explain-overlay" onClick={() => setExplainFor(null)}>
                    <div className="luna-explain-sheet" onClick={e => e.stopPropagation()}>
                        <div className="luna-explain-title">{tx('yourMessage')}</div>
                        <div className="luna-explain-original">"{messages[explainFor]?.content}"</div>
                        <div className="luna-explain-title">{tx('feedback')}</div>
                        <div className="luna-explain-body">
                            {explaining ? tx('checkingYour', { language: langLabel }) : explanation}
                        </div>
                        <button className="luna-explain-close" onClick={() => setExplainFor(null)}>{tx('close')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}